import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit3, Trash2, Save, X, Loader2, LogOut, Mail, Download, FileText, Sparkles, AlertTriangle, Send, Calendar, Users, Upload, Clock } from "lucide-react";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import ProductImage from "../components/ProductImage";
import useNoIndex from "../hooks/useNoIndex";
import RecycleBin from "../components/RecycleBin";
import ThankYouCodeReport from "../components/ThankYouCodeReport";
import RecommendationsTab from "../components/RecommendationsTab";
import NewCustomerOfferPanel from "../components/NewCustomerOfferPanel";

const FIELD_LABELS = {
  subject: "Subject", preview_text: "Preview text", body_html: "Email body",
  product_discount_percent: "Discount %", product_discount_code: "Discount code",
  custom_scheduled_for: "Send date/time",
};

// Turns FastAPI validation arrays into readable text instead of "[object Object]".
const apiErr = (e, fallback = "Request failed.") => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) {
    return d.map((x) => `${FIELD_LABELS[x.loc?.[x.loc.length - 1]] || x.loc?.[x.loc.length - 1] || "Field"}: ${x.msg}`).join("\n");
  }
  if (d && typeof d === "object") return JSON.stringify(d);
  return d || e?.message || fallback;
};

const TABS = ["Products", "Discounts", "Recommendations", "Orders", "Customers", "Invoice", "Research Library", "Campaigns", "Inquiries", "Messages", "Waitlist", "Profile"];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Products");
  useNoIndex();

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="overline">ACCESS RESTRICTED</div>
        <h1 className="font-heading text-3xl mt-3">Admin access required.</h1>
        <p className="text-sm text-[#8A94A6] mt-3">Sign in as an administrator to manage the catalog.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="overline">CONSOLE</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light mt-3">Admin Dashboard</h1>

      <div className="flex flex-wrap gap-2 mt-10 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-5 py-3 text-xs font-mono-tech uppercase tracking-[0.18em] transition-colors -mb-px ${
              tab === t ? "text-[#00C3FF] border-b border-[#00C3FF]" : "text-white/50 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "Products" && <ProductsTab />}
        {tab === "Discounts" && <DiscountsTab />}
        {tab === "Recommendations" && <RecommendationsTab />}
        {tab === "Orders" && <OrdersTab />}
        {tab === "Customers" && <CustomersTab />}
        {tab === "Invoice" && <ManualInvoiceTab />}
        {tab === "Research Library" && <BlogTab />}
        {tab === "Campaigns" && <CampaignsTab />}
        {tab === "Inquiries" && <InquiriesTab />}
        {tab === "Messages" && <MessagesTab />}
        {tab === "Waitlist" && <WaitlistTab />}
        {tab === "Profile" && <ProfileTab />}
      </div>
    </div>
  );
}

/* ---------- Products ---------- */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = () =>
    api.get("/admin/products/all").then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });

  useEffect(() => {
    load();
  }, []);

  const purgeTests = async () => {
    // First fetch the dry-run list so the admin can confirm exactly which
    // products will be deleted — no surprises.
    const { data: preview } = await api.delete("/admin/products/purge/tests");
    if (!preview.would_delete?.length) {
      alert("No test/admin/hidden products found. Nothing to purge.");
      return;
    }
    const lines = preview.would_delete.map((p) => `  • ${p.name} (${p.slug}) — ${p.reason}`).join("\n");
    if (!window.confirm(`Delete these ${preview.count} placeholder products?\n\n${lines}\n\nThis cannot be undone.`)) return;
    setPurging(true);
    try {
      const { data } = await api.delete("/admin/products/purge/tests?confirm=PURGE");
      alert(`Deleted ${data.count} product(s):\n\n${data.deleted.map((p) => `  • ${p.name}`).join("\n")}`);
      load();
    } catch (e) {
      alert(`Purge failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPurging(false);
    }
  };

  const blank = {
    slug: "",
    name: "",
    category: "",
    image: "",
    summary: "",
    variants: [{ size: "5mg", price: 49 }],
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="overline">{products.length} PRODUCTS (INCL. HIDDEN)</div>
        <div className="flex items-center gap-2">
          <button
            onClick={purgeTests}
            disabled={purging}
            className="px-3 py-2 border border-red-400/40 bg-red-400/5 text-red-300 hover:bg-red-400/10 font-mono-tech text-[10px] uppercase tracking-[0.18em] disabled:opacity-40 flex items-center gap-2"
            data-testid="admin-purge-test-products-btn"
            title="One-click clean up any test/admin/placeholder products left in the catalog"
          >
            {purging ? <Loader2 className="animate-spin" size={12} /> : <Trash2 size={12} />}
            Purge Test Products
          </button>
          <button data-testid="admin-add-product" onClick={() => setCreating(true)} className="btn-ghost">
            <Plus size={14} /> Add Product
          </button>
        </div>
      </div>

      {loading && <div className="text-[#8A94A6] font-mono-tech text-sm">Loading…</div>}

      {creating && <ProductForm initial={blank} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}

      <div className="space-y-px bg-white/5">
        {products.map((p) => (
          <div key={p.id} className="bg-[#0C0C12] border border-white/5 p-5">
            {editing === p.id ? (
              <ProductForm
                initial={p}
                onCancel={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
                isEdit
              />
            ) : (
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 vial-bg flex items-center justify-center flex-shrink-0">
                  <ProductImage product={p} className="w-[85%] h-[85%]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="overline" style={{ fontSize: 10 }}>{p.category}</div>
                    {(String(p.category).toUpperCase() === "TEST" || p.visibility === "private") && (
                      <span className="px-1.5 py-0.5 border border-red-400/40 bg-red-400/10 text-red-300 font-mono text-[9px] uppercase tracking-widest" data-testid={`admin-hidden-badge-${p.slug}`}>
                        Hidden · not in storefront
                      </span>
                    )}
                    {p.active === false && (
                      <span className="px-1.5 py-0.5 border border-amber-400/40 bg-amber-400/10 text-amber-300 font-mono text-[9px] uppercase tracking-widest">Inactive</span>
                    )}
                  </div>
                  <div className="font-heading text-base mt-1">{p.name}</div>
                  <div className="text-xs text-[#5C657A] font-mono-tech mt-1">
                    {p.variants.map((v) => `${v.size} $${v.price}`).join(" · ")}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditing(p.id)} className="p-2 text-white/60 hover:text-[#00C3FF]" data-testid={`admin-edit-${p.slug}`}>
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete ${p.name}?`)) return;
                      await api.delete(`/admin/products/${p.id}`);
                      load();
                    }}
                    className="p-2 text-white/60 hover:text-red-400"
                    data-testid={`admin-delete-${p.slug}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductForm({ initial, onCancel, onSaved, isEdit = false }) {
  const [f, setF] = useState({
    ...initial,
    variants: initial.variants?.map((v) => ({
      size: v.size || "",
      sku: v.sku || "",
      price: v.price || 0,
      tiers: (v.tiers && v.tiers.length > 0)
        ? v.tiers.map((t) => ({ min_qty: t.min_qty, unit_price: t.unit_price }))
        : [{ min_qty: 1, unit_price: v.price || 0 }],
    })) || [{ size: "", sku: "", price: 0, tiers: [{ min_qty: 1, unit_price: 0 }] }],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const updVar = (i, k) => (e) => {
    const v = [...f.variants];
    v[i] = { ...v[i], [k]: k === "price" ? Number(e.target.value) : e.target.value };
    setF({ ...f, variants: v });
  };
  const addVar = () => setF({ ...f, variants: [...f.variants, { size: "", sku: "", price: 0, tiers: [{ min_qty: 1, unit_price: 0 }] }] });
  const rmVar = (i) => setF({ ...f, variants: f.variants.filter((_, idx) => idx !== i) });

  const updTier = (vi, ti, k) => (e) => {
    const variants = [...f.variants];
    const tiers = [...variants[vi].tiers];
    tiers[ti] = { ...tiers[ti], [k]: Number(e.target.value) };
    variants[vi] = { ...variants[vi], tiers };
    setF({ ...f, variants });
  };
  const addTier = (vi) => {
    const variants = [...f.variants];
    variants[vi] = { ...variants[vi], tiers: [...variants[vi].tiers, { min_qty: 1, unit_price: 0 }] };
    setF({ ...f, variants });
  };
  const rmTier = (vi, ti) => {
    const variants = [...f.variants];
    variants[vi] = { ...variants[vi], tiers: variants[vi].tiers.filter((_, idx) => idx !== ti) };
    setF({ ...f, variants });
  };

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        slug: f.slug, name: f.name, category: f.category, image: f.image, summary: f.summary,
        variants: f.variants.map((v) => ({
          size: v.size,
          sku: v.sku || null,
          price: Number(v.price),
          tiers: v.tiers.map((t) => ({ min_qty: Number(t.min_qty), unit_price: Number(t.unit_price) })),
        })),
      };
      if (isEdit) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 border border-[#00C3FF]/30 bg-[#14141C] p-5">
      <div><label className="overline block mb-1">Slug</label><input className="input-tech" value={f.slug} onChange={upd("slug")} /></div>
      <div><label className="overline block mb-1">Name</label><input className="input-tech" value={f.name} onChange={upd("name")} /></div>
      <div><label className="overline block mb-1">Category</label><input className="input-tech" value={f.category} onChange={upd("category")} /></div>
      <div><label className="overline block mb-1">Image URL</label><input className="input-tech" value={f.image} onChange={upd("image")} /></div>
      <div className="sm:col-span-2"><label className="overline block mb-1">Summary</label><textarea rows={2} className="input-tech resize-none" value={f.summary} onChange={upd("summary")} /></div>

      <div className="sm:col-span-2">
        <div className="overline mb-3">Variants & Tier Pricing</div>
        <div className="space-y-4">
          {f.variants.map((v, vi) => (
            <div key={vi} className="border border-white/10 p-4 bg-[#0C0C12]">
              <div className="flex items-end gap-3">
                <div className="flex-1"><label className="text-[10px] font-mono-tech text-[#5C657A] block mb-1">SIZE</label><input className="input-tech" value={v.size} onChange={updVar(vi, "size")} placeholder="10mg" /></div>
                <div className="flex-1"><label className="text-[10px] font-mono-tech text-[#5C657A] block mb-1">SKU</label><input className="input-tech" value={v.sku} onChange={updVar(vi, "sku")} placeholder="RET-10MV" /></div>
                <div className="flex-1"><label className="text-[10px] font-mono-tech text-[#5C657A] block mb-1">LIST PRICE (qty 1)</label><input type="number" step="0.01" className="input-tech" value={v.price} onChange={updVar(vi, "price")} /></div>
                <button type="button" onClick={() => rmVar(vi)} className="text-white/40 hover:text-red-400 pb-3"><Trash2 size={14} /></button>
              </div>

              <div className="mt-4">
                <div className="text-[10px] font-mono-tech text-[#5C657A] tracking-[0.18em] uppercase mb-2">Tier breaks (per-unit price at qty)</div>
                <div className="space-y-2">
                  {v.tiers.map((t, ti) => (
                    <div key={ti} className="flex items-center gap-3">
                      <div className="font-mono-tech text-[10px] text-[#5C657A] w-12">qty ≥</div>
                      <input type="number" min="1" className="input-tech w-24" value={t.min_qty} onChange={updTier(vi, ti, "min_qty")} />
                      <div className="font-mono-tech text-[10px] text-[#5C657A]">@ $</div>
                      <input type="number" step="0.01" className="input-tech w-32" value={t.unit_price} onChange={updTier(vi, ti, "unit_price")} />
                      <span className="font-mono-tech text-[10px] text-[#5C657A]">/unit</span>
                      <button type="button" onClick={() => rmTier(vi, ti)} className="text-white/30 hover:text-red-400 ml-auto"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => addTier(vi)} className="btn-ghost mt-3" style={{ padding: "8px 14px", fontSize: 10 }}><Plus size={11} /> Add tier</button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={addVar} className="btn-ghost mt-3"><Plus size={12} /> Add variant</button>
      </div>

      {err && <div className="sm:col-span-2 text-xs text-red-400 font-mono-tech">{String(err)}</div>}

      <div className="sm:col-span-2 flex gap-3 pt-3 border-t border-white/5">
        <button onClick={save} disabled={busy} className="btn-primary">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {isEdit ? "Save Changes" : "Create Product"}
        </button>
        <button onClick={onCancel} className="btn-ghost"><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}

/* ---------- Discounts ---------- */
function DiscountsTab() {
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/discounts").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const blank = { code: "", percent_off: 10, min_subtotal: 0, first_order_only: false, active: true };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="overline">{list.length} CODES</div>
        <button onClick={() => setAdding(true)} className="btn-ghost" data-testid="admin-add-discount"><Plus size={14} /> Add Code</button>
      </div>

      {adding && <DiscountForm initial={blank} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}

      <div className="space-y-px bg-white/5">
        {list.map((d) => (
          <DiscountRow key={d.code} d={d} reload={load} />
        ))}
      </div>
      <ThankYouCodeReport />
    </div>
  );
}

function DiscountRow({ d, reload }) {
  const [edit, setEdit] = useState(false);
  if (edit) return <div className="bg-[#0C0C12] p-5"><DiscountForm initial={d} onCancel={() => setEdit(false)} onSaved={() => { setEdit(false); reload(); }} isEdit /></div>;

  return (
    <div className="bg-[#0C0C12] p-5 flex items-center gap-6">
      <div className="font-mono-tech text-base text-[#00C3FF] tracking-wider w-40">{d.code}</div>
      <div className="flex-1 text-xs text-[#8A94A6] font-mono-tech">
        {d.percent_off}% off
        {d.min_subtotal > 0 && ` · min $${d.min_subtotal}`}
        {d.first_order_only && " · first order"}
      </div>
      <span className={`text-[10px] font-mono-tech uppercase tracking-[0.2em] ${d.active ? "text-[#00C3FF]" : "text-[#5C657A]"}`}>{d.active ? "ACTIVE" : "INACTIVE"}</span>
      <button onClick={() => setEdit(true)} className="p-2 text-white/60 hover:text-[#00C3FF]"><Edit3 size={14} /></button>
      <button
        onClick={async () => { if (window.confirm(`Delete ${d.code}?`)) { await api.delete(`/admin/discounts/${d.code}`); reload(); } }}
        className="p-2 text-white/60 hover:text-red-400"
      ><Trash2 size={14} /></button>
    </div>
  );
}

function DiscountForm({ initial, onCancel, onSaved, isEdit = false }) {
  const [f, setF] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k, transform = (v) => v) => (e) => setF({ ...f, [k]: transform(e.target.type === "checkbox" ? e.target.checked : e.target.value) });

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        code: String(f.code).toUpperCase(),
        percent_off: Number(f.percent_off),
        min_subtotal: Number(f.min_subtotal),
        first_order_only: !!f.first_order_only,
        active: !!f.active,
      };
      if (isEdit) await api.put(`/admin/discounts/${initial.code}`, payload);
      else await api.post("/admin/discounts", payload);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 border border-[#00C3FF]/30 bg-[#14141C] p-5 mb-3">
      <div><label className="overline block mb-1">Code</label><input className="input-tech" value={f.code} disabled={isEdit} onChange={upd("code")} /></div>
      <div><label className="overline block mb-1">% off</label><input type="number" min={0} max={100} className="input-tech" value={f.percent_off} onChange={upd("percent_off")} /></div>
      <div><label className="overline block mb-1">Min subtotal</label><input type="number" min={0} step="0.01" className="input-tech" value={f.min_subtotal} onChange={upd("min_subtotal")} /></div>
      <div className="flex flex-col gap-2 justify-end">
        <label className="flex items-center gap-2 text-xs text-[#8A94A6]"><input type="checkbox" checked={f.first_order_only} onChange={upd("first_order_only")} className="accent-[#00C3FF]" /> First order only</label>
        <label className="flex items-center gap-2 text-xs text-[#8A94A6]"><input type="checkbox" checked={f.active} onChange={upd("active")} className="accent-[#00C3FF]" /> Active</label>
      </div>
      {err && <div className="sm:col-span-4 text-xs text-red-400 font-mono-tech">{String(err)}</div>}
      <div className="sm:col-span-4 flex gap-3 pt-3 border-t border-white/5">
        <button onClick={save} disabled={busy} className="btn-primary">{busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save</button>
        <button onClick={onCancel} className="btn-ghost"><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}

/* ---------- Orders (admin view) ---------- */
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const quickStatus = async (o, newStatus, e) => {
    e.stopPropagation();  // don't expand the row
    if (rowBusy) return;
    setRowBusy(o.id);
    try {
      await api.put(`/admin/orders/${o.id}`, { status: newStatus });
      await load();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Update failed");
    } finally { setRowBusy(null); }
  };

  return (
    <div className="space-y-px bg-white/5">
      {orders.length === 0 ? (
        <div className="bg-[#0C0C12] p-8 text-center text-[#5C657A] font-mono-tech text-sm">No orders yet.</div>
      ) : orders.map((o) => (
        <AdminOrderRow
          key={o.id}
          order={o}
          expanded={expanded === o.id}
          onToggle={() => setExpanded(expanded === o.id ? null : o.id)}
          onQuickStatus={(newStatus, e) => quickStatus(o, newStatus, e)}
          rowBusy={rowBusy === o.id}
          onSaved={load}
        />
      ))}
    </div>
  );
}

/* Status pipeline used by the per-row stepper and "Advance →" CTA */
const ORDER_STAGES = [
  { key: "paid",       label: "Ordered",    color: "#FFD23F" },
  { key: "processing", label: "Processing", color: "#00C3FF" },
  { key: "shipped",    label: "Shipped",    color: "#C084FC" },
  { key: "delivered",  label: "Delivered",  color: "#3DDC97" },
];

function stageIndex(status) {
  const s = (status || "").toLowerCase();
  if (s === "delivered" || s === "completed") return 3;
  if (s === "shipped") return 2;
  if (s === "processing") return 1;
  if (s === "paid") return 0;
  return -1; // awaiting_venmo, pending, refunded, cancelled etc.
}

function AdminOrderRow({ order, expanded, onToggle, onQuickStatus, rowBusy, onSaved }) {
  const status = (order.status || "").toLowerCase();
  const hasTracking = (order.tracking_number || "").trim().length > 0;
  const idx = stageIndex(status);
  const prePaidStates = new Set(["awaiting_venmo", "awaiting_payment", "pending", ""]);
  const isPrePaid = prePaidStates.has(status);
  const isTerminal = status === "delivered" || status === "completed" || status === "refunded" || status === "cancelled";
  const isPurgeable = status === "cancelled";
  const [purging, setPurging] = React.useState(false);

  const onPurge = async (e) => {
    e.stopPropagation();
    const label = order.order_number || order.id.slice(0, 8).toUpperCase();
    if (!window.confirm(`Delete order ${label} and its manual invoice record?\n\nIt will be moved to the Recycle Bin (Admin → Profile) and can be restored.`)) return;
    setPurging(true);
    try {
      await api.delete(`/admin/orders/${encodeURIComponent(order.order_number || order.id)}`);
      onSaved && onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Delete failed";
      if (detail.includes("[CANCELLED_OVERRIDE_AVAILABLE]")) {
        // eslint-disable-next-line no-alert
        const typed = window.prompt(`Order ${label} was marked paid but is cancelled and never shipped.\n\nType the order number (${label}) to delete it anyway. It goes to the Recycle Bin and can be restored.`);
        if (typed && typed.trim() === label) {
          try {
            await api.delete(`/admin/orders/${encodeURIComponent(order.order_number || order.id)}`, { params: { confirm_cancelled: label } });
            onSaved && onSaved();
          } catch (e2) {
            // eslint-disable-next-line no-alert
            alert(e2?.response?.data?.detail || "Delete failed");
          }
        }
      } else {
        // eslint-disable-next-line no-alert
        alert(detail);
      }
    } finally { setPurging(false); }
  };

  // Decide the single big primary action for this row
  let primary = null;
  if (isPrePaid) {
    primary = { stage: "paid", label: "Mark Paid · Send Receipt", color: "#FFD23F", hint: "Fires Order Confirmed email" };
  } else if (idx === 0) {
    primary = { stage: "processing", label: "Advance → Processing", color: "#00C3FF", hint: "Fires 'preparing your order' email" };
  } else if (idx === 1) {
    primary = hasTracking
      ? { stage: "shipped", label: "Advance → Shipped", color: "#C084FC", hint: "Fires shipped email with tracking link" }
      : { stage: "_need_tracking", label: "Add Tracking # to Ship", color: "#5C657A", hint: "Expands row to add tracking" };
  } else if (idx === 2) {
    primary = { stage: "delivered", label: "Advance → Delivered", color: "#3DDC97", hint: "Fires delivery confirmation email" };
  }

  const onAdvance = (e) => {
    e.stopPropagation();
    if (!primary) return;
    if (primary.stage === "_need_tracking") { onToggle(); return; }
    onQuickStatus(primary.stage, e);
  };

  // Stepper config — show even for cancelled/refunded but greyed

  return (
    <div className="bg-[#0C0C12]">
      <div className="w-full p-5 hover:bg-[#14141C] transition-colors">
        {/* Top row: identifiers + total + expand */}
        <button
          onClick={onToggle}
          className="w-full flex items-center gap-4 text-left"
          data-testid={`admin-order-${order.id}`}
        >
          <div className="font-mono-tech text-[#00C3FF] text-xs w-32 flex-shrink-0">
            {order.order_number || order.id.slice(0, 8).toUpperCase()}
          </div>
          <div className="flex-1 text-sm text-white truncate">{order.user_email}</div>
          <div className="hidden md:block text-xs text-[#5C657A] font-mono-tech">
            {new Date(order.created_at).toLocaleDateString()}
          </div>
          <div className="font-mono-tech text-base text-white w-24 text-right">
            ${Number(order.amount || order.total || 0).toFixed(2)}
          </div>
          <span
            className="text-[10px] font-mono-tech uppercase tracking-[0.2em] w-28 text-right"
            style={{ color: isTerminal && idx < 0 ? "#5C657A" : (ORDER_STAGES[idx]?.color || "#FFD23F") }}
            data-testid={`row-status-badge-${order.id}`}
          >
            {(status || "PAID").toUpperCase().replace("_", " ")}
          </span>
          <span className="font-mono-tech text-[10px] text-[#5C657A] ml-2">
            {expanded ? "▾" : "▸"}
          </span>
        </button>

        {/* Stepper + primary CTA */}
        {!isPrePaid && (
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <OrderStepper currentIndex={idx} hasTracking={hasTracking} terminal={isTerminal && idx < 3} />
            <div className="flex-1 min-w-[200px]" />
            {primary && !isTerminal && (
              <button
                onClick={onAdvance}
                disabled={rowBusy}
                data-testid={`row-advance-${order.id}`}
                title={primary.hint}
                className="px-5 py-2.5 font-mono-tech text-[11px] uppercase tracking-[0.18em] transition-all hover:brightness-125 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                style={{
                  background: `${primary.color}1a`,
                  border: `1px solid ${primary.color}80`,
                  color: primary.color,
                }}
              >
                {rowBusy ? <Loader2 className="animate-spin" size={13} /> : null}
                {primary.label}
              </button>
            )}
            {isTerminal && (
              <span
                data-testid={`row-terminal-${order.id}`}
                className="px-4 py-2 font-mono-tech text-[10px] uppercase tracking-[0.18em] border border-white/10 text-[#5C657A]"
              >
                {status === "delivered" || status === "completed" ? "✓ Fulfilled" : `Lifecycle closed · ${status}`}
              </span>
            )}
            {isPurgeable && (
              <button
                onClick={onPurge}
                disabled={purging}
                data-testid={`row-purge-${order.id}`}
                title="Permanently delete this cancelled order + its manual invoice record"
                className="px-4 py-2 border border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.18em] disabled:opacity-40 flex items-center gap-2"
              >
                {purging ? <Loader2 className="animate-spin" size={12} /> : null}
                Delete Permanently
              </button>
            )}
          </div>
        )}

        {/* Pre-paid banner — single CTA */}
        {isPrePaid && (
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-yellow-300/80">
              {status === "awaiting_venmo" ? "Awaiting Venmo verification" : "Payment not yet confirmed"}
            </div>
            <div className="flex-1" />
            <button
              onClick={onAdvance}
              disabled={rowBusy}
              data-testid={`row-mark-paid-${order.id}`}
              className="px-5 py-2.5 border border-yellow-400/60 bg-yellow-400/10 text-yellow-300 hover:bg-yellow-400/20 transition-colors font-mono-tech text-[11px] uppercase tracking-[0.18em] disabled:opacity-40 flex items-center gap-2"
            >
              {rowBusy ? <Loader2 className="animate-spin" size={13} /> : null}
              Mark Paid · Send Receipt
            </button>
          </div>
        )}
      </div>
      {expanded && <AdminOrderEditor order={order} onSaved={onSaved} />}
    </div>
  );
}

function OrderStepper({ currentIndex, hasTracking, terminal }) {
  // Visual: pill chips connected by lines. Stage is "complete" if i <= currentIndex.
  // Special case: if currentIndex == 1 (processing) and tracking is added, hint shipped is unlocked.
  return (
    <div className="flex items-center gap-0 select-none" data-testid="order-row-stepper">
      {ORDER_STAGES.map((stage, i) => {
        const complete = i <= currentIndex && !terminal;
        const isCurrent = i === currentIndex && !terminal;
        const c = terminal ? "#5C657A" : (complete ? stage.color : "#2A2A36");
        return (
          <React.Fragment key={stage.key}>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full transition-all"
                style={{
                  background: complete ? c : "transparent",
                  border: `1.5px solid ${c}`,
                  boxShadow: isCurrent ? `0 0 8px ${c}` : "none",
                }}
              />
              <span
                className="font-mono-tech text-[9px] uppercase tracking-[0.18em] whitespace-nowrap"
                style={{ color: complete ? c : "#5C657A", fontWeight: isCurrent ? 700 : 400 }}
              >
                {stage.label}
              </span>
            </div>
            {i < ORDER_STAGES.length - 1 && (
              <div
                className="h-px w-6 mx-2"
                style={{ background: i < currentIndex && !terminal ? stage.color : "#2A2A36" }}
              />
            )}
          </React.Fragment>
        );
      })}
      {currentIndex === 1 && !hasTracking && !terminal && (
        <span className="ml-3 font-mono-tech text-[9px] uppercase tracking-[0.18em] text-yellow-400/80">
          · Add tracking # to ship
        </span>
      )}
    </div>
  );
}

function AdminOrderEditor({ order, onSaved }) {
  const [f, setF] = useState({
    status: order.status || "paid",
    tracking_number: order.tracking_number || "",
    carrier: order.carrier || "",
    notes: order.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (overrides) => {
    setBusy(true);
    setActionMsg("");
    try {
      const payload = overrides ? { ...f, ...overrides } : f;
      await api.put(`/admin/orders/${order.id}`, payload);
      // If we passed overrides (one-click), sync local state so UI reflects change
      if (overrides) setF((prev) => ({ ...prev, ...overrides }));
      onSaved();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const quickAction = async (newStatus, label) => {
    await save({ status: newStatus });
    setActionMsg(`✓ ${label} — customer notified`);
    setTimeout(() => setActionMsg(""), 4000);
  };

  const status = (f.status || "").toLowerCase();
  const hasTracking = (f.tracking_number || "").trim().length > 0;
  const lines = order.pricing?.line_items || order.items || [];

  return (
    <div className="border-t border-white/5 p-6 bg-[#0a0a12]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <div className="overline mb-3">LINE ITEMS</div>
            <div className="space-y-1">
              {lines.map((li, i) => (
                <div key={i} className="flex justify-between text-xs font-mono-tech text-white/80">
                  <span>{li.name} · {li.variant} × {li.quantity}</span>
                  <span>${Number(li.line_subtotal || (li.unit_price * li.quantity) || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 space-y-1 text-xs font-mono-tech">
              {order.promo_discount > 0 && (
                <div className="flex justify-between text-[#00C3FF]"><span>Promo {order.promo_code || ''}</span><span>-${Number(order.promo_discount).toFixed(2)}</span></div>
              )}
              {order.venmo_discount > 0 && (
                <div className="flex justify-between text-[#00C3FF]"><span>Venmo 2%</span><span>-${Number(order.venmo_discount).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-white/70"><span>Shipping ({order.shipping_method || 'standard'})</span><span>{Number(order.shipping_fee || 0) === 0 ? "FREE" : `$${Number(order.shipping_fee).toFixed(2)}`}</span></div>
              {Number(order.tax_amount || 0) > 0 && (
                <div className="flex justify-between text-white/70"><span>Tax ({((order.tax_rate || 0) * 100).toFixed(2)}%)</span><span>${Number(order.tax_amount).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-white pt-1 border-t border-white/5"><span>Total</span><span>${Number(order.total || order.amount || 0).toFixed(2)}</span></div>
            </div>
          </div>
          {order.shipping_address && (
            <div data-testid={`admin-ship-address-${order.id}`}>
              <div className="overline mb-2">SHIP TO</div>
              <div className="text-xs text-white/80 leading-relaxed">
                <div className="text-white">{order.shipping_address.name}</div>
                <div>{order.shipping_address.street1}</div>
                {order.shipping_address.street2 && <div>{order.shipping_address.street2}</div>}
                <div>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</div>
                {order.shipping_address.phone && <div className="text-[#8A94A6]">{order.shipping_address.phone}</div>}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-5 self-start">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">CURRENT STATUS</div>
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#00C3FF] px-2 py-0.5 border border-[#00C3FF]/40" data-testid={`admin-order-current-status-${order.id}`}>
                {status || "—"}
              </span>
            </div>
            <div className="overline mb-2">QUICK ACTIONS</div>
            <div className="grid grid-cols-2 gap-2">
              {status === "awaiting_venmo" && (
                <button
                  data-testid={`admin-mark-paid-${order.id}`}
                  onClick={() => quickAction("paid", "Marked paid")}
                  disabled={busy}
                  className="col-span-2 px-4 py-3 border border-yellow-400/50 text-yellow-300 hover:bg-yellow-400/10 transition-colors font-mono-tech text-[11px] uppercase tracking-[0.18em] disabled:opacity-50"
                >
                  {busy ? <Loader2 className="inline animate-spin mr-2" size={14} /> : null}
                  Mark Paid · Send Receipt
                </button>
              )}
              <button
                data-testid={`admin-mark-processing-${order.id}`}
                onClick={() => quickAction("processing", "Marked processing")}
                disabled={busy || status === "processing" || status === "shipped" || status === "delivered" || status === "awaiting_venmo"}
                className="px-4 py-3 border border-[#00C3FF]/50 text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors font-mono-tech text-[11px] uppercase tracking-[0.18em] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Mark Processing
              </button>
              <button
                data-testid={`admin-mark-shipped-${order.id}`}
                onClick={() => quickAction("shipped", "Marked shipped")}
                disabled={busy || !hasTracking || status === "shipped" || status === "delivered" || status === "awaiting_venmo"}
                title={!hasTracking ? "Add a tracking number first" : ""}
                className="px-4 py-3 border border-purple-400/50 text-purple-300 hover:bg-purple-400/10 transition-colors font-mono-tech text-[11px] uppercase tracking-[0.18em] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Mark Shipped
              </button>
              <button
                data-testid={`admin-mark-delivered-${order.id}`}
                onClick={() => quickAction("delivered", "Marked delivered")}
                disabled={busy || status === "delivered" || status === "awaiting_venmo"}
                className="col-span-2 px-4 py-3 border border-green-400/50 text-green-300 hover:bg-green-400/10 transition-colors font-mono-tech text-[11px] uppercase tracking-[0.18em] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Mark Delivered
              </button>
            </div>
            {!hasTracking && (status === "paid" || status === "processing") && (
              <div className="text-[10px] font-mono-tech text-yellow-400/80 mt-2 uppercase tracking-wider">
                Enter tracking number below to enable &ldquo;Mark Shipped&rdquo;
              </div>
            )}
            {actionMsg && (
              <div data-testid={`admin-order-action-msg-${order.id}`} className="text-[10px] font-mono-tech text-green-400 mt-3 uppercase tracking-wider">
                {actionMsg}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <label className="overline block mb-1">
                Carrier
                {order.carrier_autodetected && (
                  <span className="ml-2 text-[9px] font-mono-tech text-[#00C3FF] normal-case tracking-normal">
                    · auto-detected
                  </span>
                )}
              </label>
              <input className="input-tech" value={f.carrier} onChange={upd("carrier")} placeholder="Auto-detected from tracking #" />
            </div>
            <div>
              <label className="overline block mb-1">Tracking #</label>
              <input data-testid={`admin-order-tracking-${order.id}`} className="input-tech" value={f.tracking_number} onChange={upd("tracking_number")} placeholder="1Z... / 9405... / 12-15 digits" />
            </div>
            {order.tracking_url && (
              <div className="col-span-2">
                <a
                  href={order.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`admin-order-track-link-${order.id}`}
                  className="text-[10px] font-mono-tech text-[#00C3FF] hover:underline uppercase tracking-wider"
                >
                  Open {order.carrier || "carrier"} tracking page →
                </a>
              </div>
            )}
            <div className="col-span-2">
              <label className="overline block mb-1">Internal Notes</label>
              <textarea rows={2} className="input-tech resize-none" value={f.notes} onChange={upd("notes")} />
            </div>
            <div className="col-span-2 flex items-center gap-3 flex-wrap">
              <button
                onClick={() => save()}
                disabled={busy}
                className="btn-primary"
                data-testid={`admin-order-save-${order.id}`}
              >
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save Tracking / Notes
              </button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/invoice.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const res = await api.get(`/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `ProSourceLabs-Invoice-${order.order_number || order.id.slice(0, 8)}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    // eslint-disable-next-line no-alert
                    alert("Invoice download failed: " + (err?.response?.data?.detail || err.message));
                  }
                }}
                className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.18em] text-[#00C3FF] border border-[#00C3FF]/40 px-4 py-2 hover:bg-[#00C3FF]/10"
                data-testid={`admin-order-invoice-${order.id}`}
              >
                <FileText size={14} /> Invoice PDF
              </a>
              {order.shipped_notified_at && (
                <span data-testid={`shipped-notice-badge-${order.id}`} className="text-[10px] font-mono-tech uppercase tracking-[0.18em] text-[#00C3FF] border border-[#00C3FF]/40 px-3 py-2">
                  ✓ Shipped notice sent · {new Date(order.shipped_notified_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          <div className="pt-3 border-t border-white/5">
            <button
              type="button"
              data-testid={`admin-order-advanced-toggle-${order.id}`}
              onClick={() => setAdvanced((a) => !a)}
              className="text-[10px] font-mono-tech text-[#5C657A] hover:text-white uppercase tracking-[0.18em]"
            >
              {advanced ? "▾ Hide Advanced" : "▸ Show Advanced (Refund / Cancel)"}
            </button>
            {advanced && (
              <div className="mt-3 grid grid-cols-2 gap-3" data-testid={`admin-order-advanced-${order.id}`}>
                <div className="col-span-2">
                  <label className="overline block mb-1">Override Status</label>
                  <select
                    data-testid={`admin-order-status-${order.id}`}
                    value={f.status}
                    onChange={upd("status")}
                    className="input-tech w-full"
                    style={{ colorScheme: "dark" }}
                  >
                    <option value="awaiting_venmo">Awaiting Venmo</option>
                    <option value="paid">Paid</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="refunded">Refunded</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <button
                    onClick={() => save()}
                    disabled={busy}
                    className="btn-ghost"
                    data-testid={`admin-order-save-status-${order.id}`}
                  >
                    {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Apply Status Override
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Customers ---------- */
function CustomersTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest"); // newest | spent | orders

  useEffect(() => {
    api.get("/admin/customers")
      .then(({ data }) => setList(data))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = list
    .filter((c) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      return (c.email || "").toLowerCase().includes(s)
        || (c.name || "").toLowerCase().includes(s);
    })
    .sort((a, b) => {
      if (sort === "spent") return (b.total_spent || 0) - (a.total_spent || 0);
      if (sort === "orders") return (b.order_count || 0) - (a.order_count || 0);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

  const copyAllEmails = async () => {
    const emails = filtered.map((c) => c.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      // eslint-disable-next-line no-alert
      alert(`Copied ${filtered.length} emails to clipboard.`);
    } catch {
      // eslint-disable-next-line no-alert
      alert("Could not copy. Use the CSV export instead.");
    }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await api.get("/admin/customers/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `prosource-customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("CSV download failed.");
    }
  };

  const totalLifetime = filtered.reduce((s, c) => s + (c.total_spent || 0), 0);
  const customersWithOrders = filtered.filter((c) => c.order_count > 0).length;
  const wholesaleCount = filtered.filter((c) => c.wholesale_approved).length;

  const toggleWholesale = async (c) => {
    const next = !c.wholesale_approved;
    if (!next && !window.confirm(`Revoke wholesale access from ${c.email}?`)) return;
    try {
      await api.post(`/admin/users/${c.id}/wholesale`, { approved: next });
      setList((cur) => cur.map((x) => x.id === c.id ? { ...x, wholesale_approved: next } : x));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const markVipPaid = async (c, renewal = false) => {
    const method = window.prompt(`Record the $1,950 VIP ${renewal ? "renewal" : "membership"} payment for ${c.email}.\n\nHow was it paid? Type any method, e.g. VENMO, PAYPAL, ZELLE, CASH, CHECK, WIRE.`, "");
    if (!method || !method.trim()) return;
    try {
      const { data } = await api.post(`/admin/users/${c.id}/subscription/mark-paid`, { payment_method: method.trim() });
      setList((cur) => cur.map((x) => x.id === c.id ? { ...x, subscription_paid: true, vip_renewal_due: false, total_spent: (x.total_spent || 0) + 1950, vip_membership_paid: (x.vip_membership_paid || 0) + 1950, subscription_expires_at: data.membership?.term_end && renewal ? data.membership.term_end : x.subscription_expires_at } : x));
      // eslint-disable-next-line no-alert
      alert(`${renewal ? "Renewal" : "Membership"} marked paid (${method.trim().toUpperCase()}). ${c.email} was emailed a payment confirmation.`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Mark paid failed");
    }
  };

  const resendVipLink = async (c) => {
    try {
      const { data } = await api.post(`/admin/users/${c.id}/subscription/resend`);
      // eslint-disable-next-line no-alert
      alert(data.ok ? `VIP welcome with payment link re-sent to ${data.sent_to}.` : "Resend failed.");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Resend failed");
    }
  };

  const toggleSubscription = async (c) => {
    const next = !c.subscription_active;
    if (!next && !window.confirm(`Revoke the VIP subscription for ${c.email}? VIP25 stops working immediately.`)) return;
    if (next && !window.confirm(`Approve a 90-day VIP subscription for ${c.email}? This emails them a VIP welcome with the $1,950 membership payment buttons. VIP25 unlocks once you mark the fee paid.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${c.id}/subscription`, { approved: next });
      setList((cur) => cur.map((x) => x.id === c.id ? { ...x, subscription_active: data.subscription_active, subscription_expires_at: data.subscription_expires_at, subscription_paid: false, vip_code: data.membership?.number || x.vip_code } : x));
      // eslint-disable-next-line no-alert
      if (next) alert(data.welcome_email_sent ? `VIP approved. Welcome email sent to ${c.email}.` : `VIP approved, but the welcome email to ${c.email} failed to send.`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Permanently delete ${c.email}?\n\nThis cannot be undone. Customers with fulfilled orders are protected.`)) return;
    try {
      await api.delete(`/admin/customers/${c.id}`);
      setList((cur) => cur.filter((x) => x.id !== c.id));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const wipeTestCustomers = async () => {
    // STEP 1: PREVIEW — show exactly who would be deleted, with explicit opt-in.
    let preview;
    try {
      const { data } = await api.get("/admin/customers/wipe-test/preview");
      preview = data;
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not load preview");
      return;
    }
    if (!preview.would_delete?.length && !preview.would_skip?.length) {
      // eslint-disable-next-line no-alert
      alert("No test-pattern customers found. Nothing to wipe.");
      return;
    }
    const delList = preview.would_delete.map((c) => `  · ${c.email}${c.name ? ` (${c.name})` : ""}`).join("\n") || "  (none)";
    const skipList = preview.would_skip.map((c) => {
      const why = Object.entries(c.protected_by || {}).map(([k, v]) => `${k}=${v}`).join(", ");
      return `  · ${c.email}  [PROTECTED: ${why}]`;
    }).join("\n") || "  (none)";
    const msg =
`PREVIEW — Wipe Test Customers

These ${preview.would_delete.length} accounts WILL BE PERMANENTLY DELETED:
${delList}

These ${preview.would_skip.length} accounts are PROTECTED (real activity detected) and will be SKIPPED:
${skipList}

Click OK to proceed, or Cancel to abort. This cannot be undone.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    // STEP 2: Execute.
    try {
      const { data } = await api.post("/admin/customers/wipe-test");
      setList((cur) => {
        const deletedSet = new Set(data.deleted_emails || []);
        return cur.filter((x) => !deletedSet.has(x.email));
      });
      // eslint-disable-next-line no-alert
      alert(`Deleted ${data.deleted_count} test customer(s). Skipped ${data.skipped?.length || 0}.`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Wipe failed");
    }
  };

  if (loading) {
    return <div className="text-[#5C657A] font-mono-tech text-sm">Loading customer database…</div>;
  }

  return (
    <div data-testid="admin-customers-tab" className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Customers" value={list.length} testid="stat-customers" />
        <StatCard label="With Orders" value={customersWithOrders} testid="stat-customers-active" />
        <StatCard label="Wholesale" value={wholesaleCount} testid="stat-wholesale-customers" />
        <StatCard label="Lifetime Spend" value={`$${totalLifetime.toFixed(2)}`} testid="stat-lifetime" />
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          data-testid="admin-customers-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email or name"
          className="input-tech flex-1 min-w-[220px]"
        />
        <select
          data-testid="admin-customers-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="input-tech"
          style={{ colorScheme: "dark" }}
        >
          <option value="newest">Newest signup</option>
          <option value="spent">Highest spend</option>
          <option value="orders">Most orders</option>
        </select>
        <button
          data-testid="admin-customers-copy-emails"
          onClick={copyAllEmails}
          className="btn-ghost"
          style={{ padding: "10px 16px" }}
        >
          <Mail size={14} /> Copy emails
        </button>
        <button
          data-testid="admin-customers-csv"
          onClick={downloadCsv}
          className="btn-ghost"
          style={{ padding: "10px 16px" }}
        >
          <Download size={14} /> CSV
        </button>
        <button
          data-testid="admin-customers-wipe-test"
          onClick={wipeTestCustomers}
          className="text-[10px] font-mono-tech uppercase tracking-wider border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors flex items-center gap-2"
          style={{ padding: "10px 16px" }}
          title="Preview + delete obvious test accounts (test_*, demo_*, qa_*, sample_*, new_user_*, *@test.com). Always shows you the list first. Real customers protected by orders/invoices/inquiries."
        >
          <Trash2 size={14} /> Wipe Test Customers
        </button>
      </div>

      <div className="space-y-px bg-white/5">
        {filtered.length === 0 ? (
          <div className="bg-[#0C0C12] p-8 text-center text-[#5C657A] font-mono-tech text-sm">
            {q ? "No matches." : "No customers yet."}
          </div>
        ) : filtered.map((c) => (
          <div key={c.id} className="bg-[#0C0C12] p-5 flex flex-wrap items-baseline justify-between gap-4" data-testid={`admin-customer-row-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-heading text-base text-white truncate">
                {c.name || <span className="text-[#5C657A] italic">— no name —</span>}
              </div>
              <div className="font-mono-tech text-xs text-[#00C3FF] mt-1 truncate" data-testid={`admin-customer-email-${c.id}`}>
                {c.email}
              </div>
              <div className="text-[10px] font-mono-tech text-[#5C657A] mt-2 uppercase tracking-wider flex flex-wrap gap-x-3 items-center">
                <span>Joined {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</span>
                {!c.marketing_opt_in && (
                  <span className="text-red-400">· Opted out</span>
                )}
                {c.wholesale_approved && (
                  <span className="text-[#00C3FF] border border-[#00C3FF]/40 px-2 py-0.5" data-testid={`admin-customer-wholesale-badge-${c.id}`}>
                    ★ WHOLESALE
                  </span>
                )}
                {c.subscription_active && (
                  <span className="text-[#F5C451] border border-[#F5C451]/40 px-2 py-0.5" data-testid={`admin-customer-vip-badge-${c.id}`}>
                    ★ VIP · until {new Date(c.subscription_expires_at).toLocaleDateString()}
                  </span>
                )}
                {c.subscription_active && (
                  <span data-testid={`admin-customer-vip-fee-${c.id}`} className={c.subscription_paid ? "text-[#3DDC97] border border-[#3DDC97]/40 px-2 py-0.5" : "text-[#FF7847] border border-[#FF7847]/40 px-2 py-0.5"}>
                    {c.subscription_paid ? "VIP FEE PAID" : "VIP FEE DUE · $1,950"}
                  </span>
                )}
                {c.subscription_active && c.vip_code && (
                  <span data-testid={`admin-customer-vip-code-${c.id}`} className="text-white border border-[#F5C451]/40 px-2 py-0.5 font-mono-tech">
                    CODE · {c.vip_code}
                  </span>
                )}
                {c.vip_product_used > 0 && (
                  <span data-testid={`admin-customer-vip-used-${c.id}`} className="text-[#8A94A6] border border-white/10 px-2 py-0.5">
                    VIP product used · ${c.vip_product_used.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-6 items-baseline text-xs font-mono-tech">
              <div>
                <div className="text-[10px] text-[#5C657A] uppercase tracking-wider">Orders</div>
                <div className="text-white text-lg mt-0.5">{c.order_count}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#5C657A] uppercase tracking-wider">Spent</div>
                <div className="text-[#00C3FF] text-lg mt-0.5">${(c.total_spent || 0).toFixed(2)}</div>
              </div>
              <button
                onClick={() => toggleWholesale(c)}
                data-testid={`admin-customer-wholesale-toggle-${c.id}`}
                className={`text-[10px] font-mono-tech uppercase tracking-wider px-3 py-2 border transition-colors ${
                  c.wholesale_approved
                    ? "border-red-400/40 text-red-300 hover:bg-red-400/10"
                    : "border-[#00C3FF]/40 text-[#00C3FF] hover:bg-[#00C3FF]/10"
                }`}
              >
                {c.wholesale_approved ? "Revoke Wholesale" : "+ Grant Wholesale"}
              </button>
              <button
                onClick={() => toggleSubscription(c)}
                data-testid={`admin-customer-subscription-toggle-${c.id}`}
                className={`text-[10px] font-mono-tech uppercase tracking-wider px-3 py-2 border transition-colors ${
                  c.subscription_active
                    ? "border-red-400/40 text-red-300 hover:bg-red-400/10"
                    : "border-[#F5C451]/40 text-[#F5C451] hover:bg-[#F5C451]/10"
                }`}
              >
                {c.subscription_active ? "Revoke VIP" : "+ Approve VIP Subscription"}
              </button>
              {c.subscription_active && !c.subscription_paid && (
                <>
                  <button onClick={() => markVipPaid(c)} data-testid={`admin-customer-vip-mark-paid-${c.id}`}
                    className="text-[10px] font-mono-tech uppercase tracking-wider px-3 py-2 border border-[#3DDC97]/40 text-[#3DDC97] hover:bg-[#3DDC97]/10 transition-colors">
                    Mark VIP Fee Paid
                  </button>
                  <button onClick={() => resendVipLink(c)} data-testid={`admin-customer-vip-resend-${c.id}`}
                    className="text-[10px] font-mono-tech uppercase tracking-wider px-3 py-2 border border-white/20 text-white/70 hover:bg-white/5 transition-colors">
                    Resend Payment Link
                  </button>
                </>
              )}
              {c.subscription_active && c.subscription_paid && c.vip_renewal_due && (
                <button onClick={() => markVipPaid(c, true)} data-testid={`admin-customer-vip-renewal-paid-${c.id}`}
                  className="text-[10px] font-mono-tech uppercase tracking-wider px-3 py-2 border border-[#3DDC97]/40 text-[#3DDC97] hover:bg-[#3DDC97]/10 transition-colors">
                  Mark Renewal Paid · $1,950
                </button>
              )}
              <a
                href={`mailto:${c.email}`}
                className="text-[10px] font-mono-tech text-[#00C3FF] hover:underline uppercase tracking-wider"
                data-testid={`admin-customer-email-link-${c.id}`}
              >
                Email →
              </a>
              <button
                onClick={() => deleteCustomer(c)}
                data-testid={`admin-customer-delete-${c.id}`}
                title={c.order_count > 0 ? `Has ${c.order_count} order(s) — protected from deletion if fulfilled` : "Delete this customer"}
                className="text-red-400/70 hover:text-red-300 hover:bg-red-400/10 transition-colors p-2 border border-red-400/20 hover:border-red-400/60"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="surface p-4" data-testid={testid}>
      <div className="overline text-[10px]">{label}</div>
      <div className="font-mono-tech text-2xl text-white mt-1">{value}</div>
    </div>
  );
}

/* ---------- Manual Invoice ---------- */
function ManualInvoiceTab() {
  const blankItem = {
    product_id: "",  // catalog reference (blank = custom typed item)
    variant_size: "",
    name: "",
    unit_price: "",
    quantity: 1,
    is_wholesale: false,
  };
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_street1: "",
    customer_street2: "",
    customer_city: "",
    customer_state: "",
    customer_zip: "",
    items: [{ ...blankItem }],
    discount_amount: "",
    discount_label: "",
    discount_code: "",
    shipping_fee: "",
    tax_rate: "",
    payment_status: "balance_owing",
    payment_method: "OTHER",
    invoice_number: "",
    notes: "",
    is_wholesale: false,
    tracking_number: "",
    tracking_carrier: "",  // blank = auto-detect from tracking number
    theme: "dark",  // 'dark' | 'light' (print-friendly)
    send_email: false,  // optionally email PDF to customer on generate
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [syncing, setSyncing] = useState(false);
  // Discount code catalog — fetched once so live totals reflect the
  // percent-off without a server round trip. Server re-validates on submit.
  const [discountCatalog, setDiscountCatalog] = useState([]);

  const loadHistory = () =>
    api.get("/admin/manual-invoices").then(({ data }) => setHistory(data)).catch(() => setHistory([]));
  const loadDirectory = () =>
    api.get("/admin/customer-directory").then(({ data }) => setDirectory(data || [])).catch(() => setDirectory([]));
  useEffect(() => {
    loadHistory();
    loadDirectory();
    api.get("/products").then(({ data }) => setCatalog(data || [])).catch(() => setCatalog([]));
    api.get("/admin/discounts").then(({ data }) => setDiscountCatalog(data || [])).catch(() => setDiscountCatalog([]));
  }, []);

  // When admin picks a customer from the dropdown, auto-fill every Bill To
  // field. Empty selection ("— New customer —") wipes the fields so admin
  // can start typing fresh info.
  const selectCustomerFromDirectory = (customerId) => {
    setSelectedCustomerId(customerId);
    if (!customerId) {
      setForm((prev) => ({
        ...prev,
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        customer_street1: "",
        customer_street2: "",
        customer_city: "",
        customer_state: "",
        customer_zip: "",
      }));
      return;
    }
    const c = directory.find((d) => d.id === customerId);
    if (!c) return;
    setForm((prev) => ({
      ...prev,
      customer_name: c.name || "",
      customer_phone: c.phone || "",
      customer_email: c.email || "",
      customer_street1: c.street1 || "",
      customer_street2: c.street2 || "",
      customer_city: c.city || "",
      customer_state: c.state || "",
      customer_zip: c.zip || "",
      // Trigger the same CA-tax auto-set logic from upd()
      tax_rate: (c.state || "").toUpperCase() === "CA" ? (prev.tax_rate || "0.095") : prev.tax_rate,
    }));
  };

  const syncToOrders = async () => {
    setSyncing(true);
    try {
      const { data: preview } = await api.get("/admin/invoices/sync-to-orders/preview");
      if (preview.missing_count === 0) {
        // eslint-disable-next-line no-alert
        alert(`All ${preview.total_manual_invoices} manual invoices are already synced to the Orders tab. Nothing to do.`);
        return;
      }
      const list = preview.missing.map((m) => `  · ${m.invoice_number} — ${m.customer_name || "(no name)"} — $${Number(m.total || 0).toFixed(2)} — ${m.status}`).join("\n");
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        `Sync ${preview.missing_count} manual invoice(s) into the Orders tab?\n\n` +
        `These invoices were created before the auto dual-write was added and never showed in Orders / sales totals. Syncing makes each manageable from the Orders tab (advance status, mark delivered, etc.)\n\n` +
        list +
        `\n\nThis is idempotent — re-running is safe.`
      )) return;
      const { data: result } = await api.post("/admin/invoices/sync-to-orders");
      // eslint-disable-next-line no-alert
      alert(`Synced ${result.synced_count} invoice(s) to the Orders tab. Total sales just bumped by $${result.synced.reduce((s, x) => s + Number(x.total || 0), 0).toFixed(2)}.`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // Tier-aware unit price lookup mirroring backend `tier_unit_price(variant, qty)`
  const tierUnitPrice = (variant, qty) => {
    if (!variant) return 0;
    const tiers = (variant.tiers || []).slice().sort(
      (a, b) => (a.min_qty ?? a.qty ?? 1) - (b.min_qty ?? b.qty ?? 1)
    );
    if (tiers.length === 0) return Number(variant.price || 0);
    let eff = Number(tiers[0].unit_price);
    for (const t of tiers) {
      const min = t.min_qty ?? t.qty ?? 1;
      if (qty >= min) eff = Number(t.unit_price);
    }
    return eff;
  };

  const upd = (k) => (e) => {
    const v = e.target.value;
    // Auto-set CA tax when admin picks state CA in shipping address
    if (k === "customer_state" && v.toUpperCase() === "CA") {
      setForm((prev) => ({ ...prev, [k]: v, tax_rate: prev.tax_rate || "0.095" }));
    } else if (k === "customer_state" && form.customer_state.toUpperCase() === "CA" && v.toUpperCase() !== "CA") {
      // Clear auto-set tax if user changes off CA
      setForm((prev) => ({ ...prev, [k]: v, tax_rate: prev.tax_rate === "0.095" ? "" : prev.tax_rate }));
    } else {
      setForm({ ...form, [k]: v });
    }
  };

  const updItem = (idx, k, v) => {
    const next = [...form.items];
    next[idx] = { ...next[idx], [k]: v };
    // Recompute unit_price whenever qty / variant / wholesale toggle changes
    const cur = next[idx];
    if (cur.product_id && cur.variant_size) {
      const p = catalog.find((c) => c.id === cur.product_id);
      const variant = p?.variants?.find((vv) => vv.size === cur.variant_size);
      if (variant) {
        const qty = Math.max(1, Number(cur.quantity) || 1);
        let computed;
        if (cur.is_wholesale && variant.wholesale_price) {
          computed = Number(variant.wholesale_price);
        } else {
          computed = tierUnitPrice(variant, qty);
        }
        next[idx].unit_price = computed.toFixed(2);
        next[idx].name = `${p.name} ${variant.size}`;
      }
    }
    setForm({ ...form, items: next });
  };

  const selectCatalogProduct = (idx, productId) => {
    const next = [...form.items];
    if (!productId) {
      next[idx] = { ...next[idx], product_id: "", variant_size: "", name: "", unit_price: "" };
      setForm({ ...form, items: next });
      return;
    }
    const p = catalog.find((c) => c.id === productId);
    const variant = p?.variants?.[0];
    if (!p || !variant) return;
    const qty = Math.max(1, Number(next[idx].quantity) || 1);
    const ws = next[idx].is_wholesale;
    const unit = ws && variant.wholesale_price
      ? Number(variant.wholesale_price)
      : tierUnitPrice(variant, qty);
    next[idx] = {
      ...next[idx],
      product_id: p.id,
      variant_size: variant.size,
      name: `${p.name} ${variant.size}`,
      unit_price: unit.toFixed(2),
    };
    setForm({ ...form, items: next });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...blankItem }] });
  const removeItem = (idx) =>
    setForm({ ...form, items: form.items.length > 1 ? form.items.filter((_, i) => i !== idx) : form.items });

  // Live totals
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const subtotal = form.items.reduce((s, it) => s + num(it.unit_price) * num(it.quantity), 0);

  // Resolve typed discount code against the cached catalog (case-insensitive,
  // must be active + have a percent_off). Enforces the same restrict_slugs /
  // restrict_min_variant_quantity / restrict_min_total_quantity rules as the
  // backend so the live preview matches what the server will accept.
  const codeRaw = (form.discount_code || "").trim().toUpperCase();
  const matchedCodeRaw = codeRaw
    ? discountCatalog.find(
        (c) => (c.code || "").toUpperCase() === codeRaw && c.active && Number(c.percent_off) > 0
      )
    : null;
  const codeInvalidLookup = codeRaw && !matchedCodeRaw;

  // Check restrictions against current line items.
  const codeRestrictionErr = (() => {
    if (!matchedCodeRaw) return "";
    const slugs = (matchedCodeRaw.restrict_slugs || []).map((s) => (s || "").toLowerCase());
    const minVarQty = Number(matchedCodeRaw.restrict_min_variant_quantity || 0);
    const minTotQty = Number(matchedCodeRaw.restrict_min_total_quantity || 0);
    if (!slugs.length && !minVarQty && !minTotQty) return "";

    const nameMatchesSlug = (slug, name) => {
      const n = (name || "").toLowerCase();
      return n.includes(slug) || n.includes(slug.replace(/-/g, " ")) || n.includes(slug.replace(/-/g, ""));
    };
    let eligibleQty = 0;
    let eligibleSubtotal = 0;
    form.items.forEach((it) => {
      if (slugs.length && !slugs.some((s) => nameMatchesSlug(s, it.name))) return;
      if (minVarQty && num(it.quantity) < minVarQty) return;
      eligibleSubtotal += num(it.unit_price) * num(it.quantity);
      eligibleQty += num(it.quantity);
    });

    if (minTotQty && eligibleQty < minTotQty) {
      return `Requires at least ${minTotQty} qualifying units on the invoice.`;
    }
    if ((slugs.length || minVarQty) && eligibleSubtotal <= 0) {
      if (slugs.length && minVarQty) return `Requires ${minVarQty}+ of: ${slugs.join(", ")}.`;
      if (minVarQty) return `Requires at least ${minVarQty} units per line.`;
      if (slugs.length) return `Applies only to: ${slugs.join(", ")}.`;
    }
    return "";
  })();

  // Only apply discount when the code lookup succeeded AND restrictions pass.
  const matchedCode = matchedCodeRaw && !codeRestrictionErr ? matchedCodeRaw : null;
  const codeInvalid = codeInvalidLookup || (matchedCodeRaw && !!codeRestrictionErr);

  // Discount base — eligible-only when restricted, full subtotal otherwise.
  const codeDiscount = (() => {
    if (!matchedCode) return 0;
    const slugs = (matchedCode.restrict_slugs || []).map((s) => (s || "").toLowerCase());
    const minVarQty = Number(matchedCode.restrict_min_variant_quantity || 0);
    if (!slugs.length && !minVarQty) {
      return +(subtotal * (Number(matchedCode.percent_off) / 100)).toFixed(2);
    }
    const nameMatchesSlug = (slug, name) => {
      const n = (name || "").toLowerCase();
      return n.includes(slug) || n.includes(slug.replace(/-/g, " ")) || n.includes(slug.replace(/-/g, ""));
    };
    let base = 0;
    form.items.forEach((it) => {
      if (slugs.length && !slugs.some((s) => nameMatchesSlug(s, it.name))) return;
      if (minVarQty && num(it.quantity) < minVarQty) return;
      base += num(it.unit_price) * num(it.quantity);
    });
    return +(base * (Number(matchedCode.percent_off) / 100)).toFixed(2);
  })();

  const discount = matchedCode ? codeDiscount : Math.max(0, num(form.discount_amount));
  const shipping = Math.max(0, num(form.shipping_fee));
  const taxRate = Math.max(0, num(form.tax_rate));
  const taxBase = Math.max(0, subtotal - discount);
  const tax = +(taxBase * taxRate).toFixed(2);
  const total = +(taxBase + shipping + tax).toFixed(2);

  // Suggested shipping — matches the online checkout pricing engine:
  //   standard = $12.99, FREE at $150+ subtotal (post-discount).
  // Admin can override by typing directly in the field; the "Auto-fill"
  // button one-clicks the suggested rate. This closes the gap where admin-
  // created manual invoices used to launch with $0 shipping by default.
  const suggestedShipping = taxBase >= 150 ? 0 : 12.99;
  const autoFillShipping = () => setForm({ ...form, shipping_fee: suggestedShipping.toFixed(2) });

  const generate = async () => {
    setErr("");
    if (!form.customer_name.trim()) { setErr("Customer name is required."); return; }
    const validItems = form.items.filter((it) => it.name.trim() && num(it.unit_price) >= 0 && num(it.quantity) >= 1);
    if (validItems.length === 0) { setErr("Add at least one line item."); return; }
    setBusy(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim() || null,
        customer_email: form.customer_email.trim() || null,
        customer_street1: form.customer_street1.trim() || null,
        customer_street2: form.customer_street2.trim() || null,
        customer_city: form.customer_city.trim() || null,
        customer_state: form.customer_state.trim() || null,
        customer_zip: form.customer_zip.trim() || null,
        items: validItems.map((it) => ({
          name: it.name.trim(),
          unit_price: num(it.unit_price),
          quantity: num(it.quantity),
          is_wholesale: !!it.is_wholesale,
        })),
        discount_amount: discount,
        discount_label: form.discount_label.trim() || null,
        discount_code: form.discount_code.trim() || null,
        shipping_fee: shipping,
        tax_rate: taxRate,
        payment_status: form.payment_status,
        payment_method: form.payment_method.trim() || "OTHER",
        invoice_number: form.invoice_number.trim() || null,
        notes: form.notes.trim() || null,
        is_wholesale: !!form.is_wholesale,
        tracking_number: form.tracking_number.trim() || null,
        tracking_carrier: form.tracking_carrier.trim() || null,
        theme: form.theme || "dark",
        send_email: !!form.send_email,
      };
      const res = await api.post("/admin/manual-invoice", payload, { responseType: "blob" });
      const invoiceNo = res.headers["x-invoice-number"] || "manual";
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ProSourceLabs-Invoice-${invoiceNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      loadHistory();
      loadDirectory();  // refresh so any newly-auto-created customer shows in the dropdown next time
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to generate invoice.");
    } finally {
      setBusy(false);
    }
  };

  const downloadHistory = async (id, no, theme = "dark") => {
    try {
      const res = await api.get(`/admin/manual-invoices/${id}/pdf`, {
        params: { theme },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      // Print-friendly re-downloads get a "-print" suffix so admins can
      // keep both PDFs side-by-side without one overwriting the other.
      a.download = `ProSourceLabs-Invoice-${no}${theme === "light" ? "-print" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Re-download failed");
    }
  };

  return (
    <div className="space-y-6">
      <QuickLegacyInvoice catalog={catalog} tierUnitPrice={tierUnitPrice} onSaved={loadHistory} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-8" data-testid="manual-invoice-form">
        <div className="surface p-6 sm:p-8 space-y-6">
          <div>
            <div className="overline">MANUAL INVOICE</div>
            <h2 className="font-heading text-2xl mt-2 font-light">Generate a one-off invoice</h2>
            <p className="text-sm text-[#8A94A6] mt-1">
              For in-person or off-platform transactions. Generates the same branded PDF as our automated invoices — no shipping workflow attached.
            </p>
          </div>

          {/* Bill To */}
          <div>
            <div className="overline mb-3">BILL TO</div>

            {/* Customer directory picker — auto-fills every field below.
                Empty option lets admin start fresh for a new walk-in. */}
            <div className="mb-4">
              <label className="block text-[10px] font-mono-tech uppercase tracking-[0.18em] text-[#8A94A6] mb-1.5">
                Customer directory <span className="text-[#00C3FF]">({directory.length} on file)</span>
              </label>
              <select
                data-testid="manual-customer-directory-picker"
                className="input-tech w-full"
                value={selectedCustomerId}
                onChange={(e) => selectCustomerFromDirectory(e.target.value)}
              >
                <option value="">— New / walk-in customer (type info below) —</option>
                {directory.map((c) => {
                  const parts = [c.name, c.email, c.phone].filter(Boolean);
                  const cityState = [c.city, c.state].filter(Boolean).join(", ");
                  const label = parts.join(" · ") + (cityState ? ` · ${cityState}` : "");
                  return (
                    <option key={c.id} value={c.id}>{label || c.email || "(no info)"}</option>
                  );
                })}
              </select>
              <p className="text-[11px] text-[#8A94A6] mt-1.5">
                Pick an existing customer to autofill the fields below. New customers you type get saved automatically on invoice generation.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input
                data-testid="manual-customer-name"
                className="input-tech col-span-2"
                placeholder="Customer full name *"
                value={form.customer_name}
                onChange={upd("customer_name")}
              />
              <input className="input-tech" placeholder="Phone" value={form.customer_phone} onChange={upd("customer_phone")} />
              <input className="input-tech" placeholder="Email" value={form.customer_email} onChange={upd("customer_email")} />
              <input className="input-tech col-span-2" placeholder="Street address" value={form.customer_street1} onChange={upd("customer_street1")} />
              <input className="input-tech col-span-2" placeholder="Apt / Suite (optional)" value={form.customer_street2} onChange={upd("customer_street2")} />
              <input className="input-tech" placeholder="City" value={form.customer_city} onChange={upd("customer_city")} />
              <div className="grid grid-cols-2 gap-3">
                <input className="input-tech" placeholder="State" value={form.customer_state} onChange={upd("customer_state")} />
                <input className="input-tech" placeholder="ZIP" value={form.customer_zip} onChange={upd("customer_zip")} />
              </div>
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">LINE ITEMS</div>
              <button
                type="button"
                data-testid="manual-item-add"
                onClick={addItem}
                className="text-[10px] font-mono-tech uppercase tracking-[0.18em] text-[#00C3FF] border border-[#00C3FF]/40 px-3 py-1.5 hover:bg-[#00C3FF]/10"
              >
                <Plus size={11} className="inline mr-1" /> Add row
              </button>
            </div>
            <div className="space-y-4">
              {form.items.map((it, idx) => {
                const linkedProduct = catalog.find((c) => c.id === it.product_id);
                const linkedVariant = linkedProduct?.variants?.find((v) => v.size === it.variant_size);
                const lineTotal = (Number(it.unit_price) || 0) * (Number(it.quantity) || 0);
                return (
                  <div key={idx} className="border border-white/10 p-3 space-y-2" data-testid={`manual-item-row-${idx}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <select
                        className="input-tech col-span-5"
                        value={it.product_id}
                        onChange={(e) => selectCatalogProduct(idx, e.target.value)}
                        data-testid={`manual-item-product-${idx}`}
                      >
                        <option value="">— Custom / non-catalog —</option>
                        {catalog.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      <select
                        className="input-tech col-span-2 disabled:opacity-40"
                        value={it.variant_size}
                        onChange={(e) => updItem(idx, "variant_size", e.target.value)}
                        disabled={!linkedProduct}
                        data-testid={`manual-item-variant-${idx}`}
                      >
                        <option value="">Size</option>
                        {linkedProduct?.variants?.map((v) => (
                          <option key={v.size} value={v.size}>{v.size}</option>
                        ))}
                      </select>
                      <input
                        className="input-tech col-span-2"
                        placeholder="Unit $"
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.unit_price}
                        onChange={(e) => updItem(idx, "unit_price", e.target.value)}
                      />
                      <input
                        className="input-tech col-span-1"
                        placeholder="Qty"
                        type="number"
                        min="1"
                        value={it.quantity}
                        onChange={(e) => updItem(idx, "quantity", e.target.value)}
                      />
                      <label className="col-span-1 flex items-center gap-1 text-[10px] font-mono-tech text-[#8A94A6] uppercase justify-center">
                        <input
                          type="checkbox"
                          checked={!!it.is_wholesale}
                          onChange={(e) => updItem(idx, "is_wholesale", e.target.checked)}
                        />
                        WS
                      </label>
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="col-span-1 text-[#8A94A6] hover:text-red-400 flex items-center justify-center"
                        aria-label="Remove row"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    {/* Custom item name field (only for non-catalog rows) */}
                    {!linkedProduct && (
                      <input
                        className="input-tech"
                        placeholder="Custom product name (e.g. Syringes 10pk)"
                        value={it.name}
                        onChange={(e) => updItem(idx, "name", e.target.value)}
                        data-testid={`manual-item-name-${idx}`}
                      />
                    )}
                    {/* Catalog product preview */}
                    {linkedProduct && linkedVariant && (
                      <div className="flex items-center justify-between text-[11px] font-mono-tech text-[#5C657A] gap-3 flex-wrap">
                        <span className="text-white">{linkedProduct.name} {linkedVariant.size}</span>
                        <span>
                          {it.is_wholesale && linkedVariant.wholesale_price ? (
                            <>WS · ${Number(linkedVariant.wholesale_price).toFixed(2)}/vial × {it.quantity || 1} = <span className="text-[#00C3FF]">${lineTotal.toFixed(2)}</span></>
                          ) : (
                            <>Tier @ qty {it.quantity || 1} → ${Number(it.unit_price || 0).toFixed(2)}/ea = <span className="text-[#00C3FF]">${lineTotal.toFixed(2)}</span></>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider mt-2">
              Pick from catalog to auto-fill name + tier price. Toggle &quot;WS&quot; for wholesale per-vial pricing.
              Unit price auto-recalculates on quantity change.
            </p>
          </div>

          {/* Adjustments */}
          <div>
            <div className="overline mb-3">ADJUSTMENTS</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 sm:col-span-4">
                <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">
                  Discount code <span className="text-[#5C657A] normal-case">(applies % off subtotal)</span>
                </label>
                <input
                  className="input-tech mt-1 uppercase"
                  placeholder="e.g. RETA30, WELCOME5"
                  value={form.discount_code}
                  onChange={upd("discount_code")}
                  data-testid="manual-invoice-discount-code"
                />
                {matchedCode && (
                  <div className="text-[11px] font-mono-tech text-[#3DDC97] mt-1" data-testid="manual-invoice-discount-code-applied">
                    {matchedCode.code} · {Number(matchedCode.percent_off)}% off · -${codeDiscount.toFixed(2)}
                  </div>
                )}
                {codeInvalid && (
                  <div className="text-[11px] font-mono-tech text-red-300 mt-1" data-testid="manual-invoice-discount-code-invalid">
                    {codeRestrictionErr || "Code not found or inactive"}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">
                  Discount $ {matchedCode && <span className="text-[#5C657A] normal-case">(overridden by code)</span>}
                </label>
                <input
                  className="input-tech mt-1"
                  type="number"
                  step="0.01"
                  min="0"
                  value={matchedCode ? codeDiscount.toFixed(2) : form.discount_amount}
                  onChange={upd("discount_amount")}
                  disabled={!!matchedCode}
                />
              </div>
              <div>
                <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Discount label</label>
                <input
                  className="input-tech mt-1"
                  placeholder="e.g. FRIEND10"
                  value={matchedCode ? matchedCode.code : form.discount_label}
                  onChange={upd("discount_label")}
                  disabled={!!matchedCode}
                />
              </div>
              <div>
                <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">
                  Shipping $ <span className="text-[#3DDC97]">· suggested: {suggestedShipping === 0 ? "FREE ($150+)" : `$${suggestedShipping.toFixed(2)}`}</span>
                </label>
                <div className="flex gap-2 mt-1">
                  <input
                    className="input-tech flex-1"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.shipping_fee}
                    onChange={upd("shipping_fee")}
                  />
                  <button
                    type="button"
                    onClick={autoFillShipping}
                    className="text-[10px] font-mono-tech text-[#00C3FF] uppercase tracking-wider border border-[#00C3FF]/40 hover:bg-[#00C3FF]/10 px-2"
                    data-testid="manual-invoice-shipping-autofill"
                    title="Auto-fill shipping based on subtotal — matches the online checkout rate ($12.99, or FREE at $150+)"
                  >
                    Auto-Fill
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Tax rate (0.095 = 9.5% · auto when state = CA)</label>
                <input
                  className="input-tech mt-1"
                  type="number"
                  step="0.001"
                  min="0"
                  value={form.tax_rate}
                  onChange={upd("tax_rate")}
                />
              </div>
            </div>
          </div>

          {/* Payment + metadata */}
          <div>
            <div className="overline mb-3">PAYMENT &amp; METADATA</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Status stamp</label>
                <select
                  className="input-tech mt-1"
                  value={form.payment_status}
                  onChange={upd("payment_status")}
                  data-testid="manual-payment-status"
                >
                  <option value="balance_owing">Balance Owing</option>
                  <option value="paid">Paid in Full</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Payment method label</label>
                <select className="input-tech mt-1" value={form.payment_method} onChange={upd("payment_method")}>
                  <option value="CASH">Cash</option>
                  <option value="VENMO">Venmo</option>
                  <option value="PAYPAL">PayPal</option>
                  <option value="ZELLE">Zelle</option>
                  <option value="WIRE">Wire</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Invoice # (blank = auto)</label>
                <input className="input-tech mt-1" placeholder="MAN-0001" value={form.invoice_number} onChange={upd("invoice_number")} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  id="is_wholesale_flag"
                  type="checkbox"
                  checked={form.is_wholesale}
                  onChange={(e) => setForm({ ...form, is_wholesale: e.target.checked })}
                />
                <label htmlFor="is_wholesale_flag" className="text-[10px] font-mono-tech text-[#8A94A6] uppercase tracking-wider">
                  Stamp as wholesale order
                </label>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="overline mb-3 text-[#00C3FF]">TRACKING (OPTIONAL)</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Tracking number</label>
                  <input
                    className="input-tech mt-1"
                    placeholder="1Z999AA10123456784 / 9400... / 76122..."
                    value={form.tracking_number}
                    onChange={upd("tracking_number")}
                    data-testid="manual-invoice-tracking-number"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Carrier (auto-detect)</label>
                  <select
                    className="input-tech mt-1"
                    value={form.tracking_carrier}
                    onChange={upd("tracking_carrier")}
                    data-testid="manual-invoice-tracking-carrier"
                  >
                    <option value="">Auto-detect</option>
                    <option value="UPS">UPS</option>
                    <option value="USPS">USPS</option>
                    <option value="FEDEX">FedEx</option>
                    <option value="DHL">DHL</option>
                  </select>
                </div>
              </div>
              <p className="text-[10px] font-mono-tech text-[#5C657A] mt-2 uppercase tracking-wider">
                Leave blank to skip. When set, the PDF shows a cyan tracking strip with a clickable carrier link and the SHIPPED milestone lights up on the status bar.
              </p>
            </div>
            <div className="mt-3">
              <label className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Internal notes (not shown on PDF)</label>
              <textarea rows={2} className="input-tech mt-1 resize-none" value={form.notes} onChange={upd("notes")} />
            </div>
          </div>

          {err && (
            <div className="text-[11px] text-red-300 font-mono-tech uppercase tracking-wider" data-testid="manual-invoice-error">
              {err}
            </div>
          )}

          {/* Theme + delivery options */}
          <div className="border-t border-white/5 pt-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2" data-testid="manual-invoice-theme-toggle">
              <span className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider">Theme:</span>
              <button
                type="button"
                onClick={() => setForm({ ...form, theme: "dark" })}
                className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-[0.18em] border transition-colors ${
                  form.theme === "dark"
                    ? "border-[#00C3FF] bg-[#00C3FF]/10 text-[#00C3FF]"
                    : "border-white/20 text-[#8A94A6] hover:text-white"
                }`}
                data-testid="manual-invoice-theme-dark"
              >
                ◐ Dark
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, theme: "light" })}
                className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-[0.18em] border transition-colors ${
                  form.theme === "light"
                    ? "border-[#00C3FF] bg-[#00C3FF]/10 text-[#00C3FF]"
                    : "border-white/20 text-[#8A94A6] hover:text-white"
                }`}
                data-testid="manual-invoice-theme-light"
              >
                ◑ Light (print)
              </button>
            </div>
            <label className="flex items-center gap-2 text-[10px] font-mono-tech text-[#8A94A6] uppercase tracking-wider cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.send_email}
                onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                data-testid="manual-invoice-send-email"
                disabled={!form.customer_email.trim()}
              />
              Email PDF to customer on generate
              {!form.customer_email.trim() && (
                <span className="text-[#5C657A] normal-case">(requires customer email)</span>
              )}
            </label>
          </div>

          <button
            type="button"
            onClick={generate}
            disabled={busy}
            className="btn-primary"
            data-testid="manual-invoice-generate"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
            {busy ? "Generating..." : "Generate & Download Invoice PDF"}
          </button>
        </div>
      </div>

      {/* Right pane: Totals preview + history */}
      <div className="space-y-8">
        <div className="surface p-6">
          <div className="overline">TOTAL PREVIEW</div>
          <div className="mt-4 space-y-2 text-sm font-mono-tech">
            <div className="flex justify-between"><span className="text-[#8A94A6]">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-[#00C3FF]"><span>Discount</span><span>-${discount.toFixed(2)}</span></div>
            )}
            {shipping > 0 && (
              <div className="flex justify-between"><span className="text-[#8A94A6]">Shipping</span><span>${shipping.toFixed(2)}</span></div>
            )}
            {tax > 0 && (
              <div className="flex justify-between"><span className="text-[#8A94A6]">Tax</span><span>${tax.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between border-t border-white/10 pt-3 mt-3">
              <span className="text-[#00C3FF] uppercase tracking-wider text-xs">TOTAL DUE</span>
              <span className="text-[#00C3FF] text-lg" data-testid="manual-invoice-total">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="surface p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">RECENT MANUAL INVOICES</div>
            <button
              onClick={syncToOrders}
              disabled={syncing}
              data-testid="manual-invoice-sync-to-orders"
              title="Back-fills any manual invoices that never made it into the Orders tab (pre-dual-write entries). Idempotent — safe to re-run. Shows preview before sync."
              className="text-[10px] font-mono-tech uppercase tracking-wider border border-[#00C3FF]/40 text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors px-3 py-2 disabled:opacity-40 flex items-center gap-2"
            >
              {syncing ? <Loader2 className="animate-spin" size={12} /> : null}
              ⇆ Sync → Orders
            </button>
          </div>
          {history.length === 0 ? (
            <div className="text-xs text-[#5C657A]">No manual invoices yet.</div>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h.id} className="border border-white/5 p-3 flex items-center justify-between gap-3" data-testid={`manual-invoice-history-${h.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-tech text-xs text-[#00C3FF]">{h.invoice_number}</div>
                    <div className="text-xs text-white truncate">{h.customer_name}</div>
                    <div className="text-[10px] font-mono-tech text-[#5C657A]">
                      {new Date(h.created_at).toLocaleDateString()} · {h.payment_method}
                      {(["paid", "processing", "shipped", "delivered", "complete", "refunded"].includes(h.status)) ? " · PAID" : " · OWING"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-tech text-sm">${Number(h.total).toFixed(2)}</div>
                    <div className="flex flex-col items-end gap-1 mt-1">
                      {!["paid", "processing", "shipped", "delivered", "complete", "refunded"].includes(h.status) && (
                        <button
                          onClick={async () => {
                            try {
                              await api.post(`/admin/manual-invoices/${h.id}/mark-paid`);
                              await loadHistory();
                            } catch (e) {
                              // eslint-disable-next-line no-alert
                              alert("Mark-paid failed: " + (e?.response?.data?.detail || e.message));
                            }
                          }}
                          className="text-[10px] font-mono-tech text-[#3DDC97] uppercase tracking-wider hover:underline"
                          data-testid={`manual-invoice-mark-paid-${h.invoice_number}`}
                          title="Mark this invoice paid — syncs to the Orders tab automatically"
                        >
                          ✓ Mark Paid
                        </button>
                      )}
                      <button
                        onClick={() => downloadHistory(h.id, h.invoice_number, "dark")}
                        className="text-[10px] font-mono-tech text-[#00C3FF] uppercase tracking-wider hover:underline"
                        data-testid={`manual-invoice-redownload-dark-${h.invoice_number}`}
                        title="Re-download the on-brand dark PDF (best for email)"
                      >
                        <FileText size={10} className="inline mr-1" /> Re-download
                      </button>
                      <button
                        onClick={() => downloadHistory(h.id, h.invoice_number, "light")}
                        className="text-[10px] font-mono-tech text-[#8A94A6] uppercase tracking-wider hover:text-white hover:underline"
                        data-testid={`manual-invoice-redownload-print-${h.invoice_number}`}
                        title="Re-download the white/print-friendly PDF (best for physical printing)"
                      >
                        <FileText size={10} className="inline mr-1" /> Print (White)
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}


/* ---------- Quick Legacy Invoice Add ---------- */
function QuickLegacyInvoice({ catalog, tierUnitPrice, onSaved }) {
  // Compact rapid-entry form for historical invoices. Uses the same catalog
  // + tier-pricing helpers as the full Manual Invoice form so unit prices
  // auto-populate from the SKU + qty.
  const emptyItem = () => ({
    product_id: "",     // catalog reference; blank = custom typed item
    variant_size: "",   // selected variant
    name: "",           // display name
    unit_price: "",
    quantity: 1,
  });
  const empty = () => ({
    invoice_number: "",
    invoice_date: "",
    customer_name: "",
    customer_phone: "",
    payment_status: "balance_owing",
    items: [emptyItem()],
    discount_amount: "",
    shipping_fee: "",
    tax_rate: "",
    target_total: "",
  });
  const [f, setF] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const updField = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  // Updates a single field on a single item, then re-runs tier-aware pricing.
  const updItem = (i, k, v) => {
    setF((prev) => {
      const items = [...prev.items];
      items[i] = { ...items[i], [k]: v };
      const cur = items[i];
      // Recompute unit_price when product/variant/qty changes
      if (cur.product_id && cur.variant_size && (k === "product_id" || k === "variant_size" || k === "quantity")) {
        const p = (catalog || []).find((c) => c.id === cur.product_id);
        const variant = p?.variants?.find((vv) => vv.size === cur.variant_size);
        if (variant) {
          const qty = Math.max(1, Number(cur.quantity) || 1);
          items[i].unit_price = tierUnitPrice(variant, qty).toFixed(2);
          items[i].name = `${p.name} ${variant.size}`;
        }
      }
      return { ...prev, items };
    });
  };

  // When admin picks a product, auto-select first variant + auto-fill name/price.
  const onPickProduct = (i, productId) => {
    setF((prev) => {
      const items = [...prev.items];
      if (!productId) {
        items[i] = { ...items[i], product_id: "", variant_size: "", name: "", unit_price: "" };
        return { ...prev, items };
      }
      const p = (catalog || []).find((c) => c.id === productId);
      const variant = p?.variants?.[0];
      if (!p || !variant) return prev;
      const qty = Math.max(1, Number(items[i].quantity) || 1);
      const unit = tierUnitPrice(variant, qty);
      items[i] = {
        ...items[i],
        product_id: productId,
        variant_size: variant.size,
        name: `${p.name} ${variant.size}`,
        unit_price: unit.toFixed(2),
      };
      return { ...prev, items };
    });
  };

  const addItem = () => setF((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));
  const removeItem = (i) => setF((prev) => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));

  const itemsSubtotal = f.items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.quantity) || 0), 0);
  const computedTotal = Math.max(
    0,
    itemsSubtotal - (Number(f.discount_amount) || 0) + (Number(f.shipping_fee) || 0) + itemsSubtotal * (Number(f.tax_rate) || 0)
  );

  const submit = async () => {
    if (!f.customer_name.trim()) {
      // eslint-disable-next-line no-alert
      alert("Customer name is required.");
      return;
    }
    const validItems = f.items.filter((it) => (it.name || "").trim() && Number(it.unit_price) > 0 && Number(it.quantity) > 0);
    if (!validItems.length) {
      // eslint-disable-next-line no-alert
      alert("Add at least one line item with product, unit price, and quantity.");
      return;
    }
    // Auto-adjust discount to make the saved total match the user's target_total
    let discount = Number(f.discount_amount) || 0;
    const target = Number(f.target_total);
    if (f.target_total && !Number.isNaN(target) && Math.abs(target - computedTotal) > 0.01) {
      discount += computedTotal - target;
    }
    const payload = {
      invoice_number: f.invoice_number.trim() || undefined,
      invoice_date: f.invoice_date ? new Date(f.invoice_date).toISOString() : undefined,
      customer_name: f.customer_name.trim(),
      customer_phone: f.customer_phone.trim() || undefined,
      items: validItems.map((it) => ({
        name: it.name.trim(),
        unit_price: Number(it.unit_price),
        quantity: Number(it.quantity),
        is_wholesale: false,
      })),
      discount_amount: discount,
      discount_label: discount > 0 ? "Adjustment" : undefined,
      shipping_fee: Number(f.shipping_fee) || 0,
      tax_rate: Number(f.tax_rate) || 0,
      payment_status: f.payment_status,
      payment_method: "OTHER",
      notes: "Imported via Quick Legacy Add",
      theme: "light",
      send_email: false,
    };
    setBusy(true);
    try {
      await api.post("/admin/manual-invoice", payload, { responseType: "blob" });
      setLastSaved({
        invoice_number: payload.invoice_number || "auto",
        customer_name: payload.customer_name,
        total: (Number(f.target_total) || computedTotal),
      });
      setF(empty());
      if (onSaved) onSaved();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!expanded) {
    return (
      <div className="surface p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="quick-legacy-invoice-collapsed">
        <div>
          <div className="overline">QUICK LEGACY INVOICE ADD</div>
          <p className="text-xs text-[#8A94A6] mt-1">
            Rapid-fire back-fill historical paper/PDF invoices. Auto-fills tier pricing from the catalog. Each save dual-writes to Orders + sales totals.
          </p>
        </div>
        <button
          onClick={() => setExpanded(true)}
          data-testid="quick-legacy-invoice-open"
          className="self-start sm:self-auto px-4 py-2 border border-[#00C3FF]/40 text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.18em] flex items-center gap-2 shrink-0"
        >
          <Plus size={12} /> Open Quick Add
        </button>
      </div>
    );
  }

  return (
    <div className="surface p-4 sm:p-6 space-y-5" data-testid="quick-legacy-invoice-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline">QUICK LEGACY INVOICE ADD</div>
          <p className="text-xs text-[#8A94A6] mt-1">Form stays open after each save for rapid-fire entry.</p>
        </div>
        <button
          onClick={() => setExpanded(false)}
          className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider hover:text-white shrink-0"
        >
          Collapse
        </button>
      </div>

      {lastSaved && (
        <div
          className="border border-green-400/40 bg-green-400/5 p-3 text-xs font-mono-tech text-green-300 flex items-center justify-between gap-3"
          data-testid="quick-legacy-invoice-last-saved"
        >
          <span className="truncate">✓ Saved {lastSaved.invoice_number} — {lastSaved.customer_name} — ${Number(lastSaved.total).toFixed(2)}</span>
          <button onClick={() => setLastSaved(null)} className="text-green-300/60 hover:text-green-300 shrink-0">×</button>
        </div>
      )}

      {/* Header fields — stacked on mobile, 2x2 on sm+, 4 cols on lg */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="overline block mb-1">Invoice #</label>
          <input
            data-testid="quick-legacy-invoice-number"
            className="input-tech w-full"
            placeholder="06-0194"
            value={f.invoice_number}
            onChange={updField("invoice_number")}
          />
        </div>
        <div>
          <label className="overline block mb-1">Date</label>
          <input
            data-testid="quick-legacy-invoice-date"
            type="date"
            className="input-tech w-full"
            style={{ colorScheme: "dark" }}
            value={f.invoice_date}
            onChange={updField("invoice_date")}
          />
        </div>
        <div>
          <label className="overline block mb-1">Customer name *</label>
          <input
            data-testid="quick-legacy-customer-name"
            className="input-tech w-full"
            placeholder="Kevin Barber"
            value={f.customer_name}
            onChange={updField("customer_name")}
          />
        </div>
        <div>
          <label className="overline block mb-1">Phone</label>
          <input
            data-testid="quick-legacy-customer-phone"
            className="input-tech w-full"
            placeholder="(555) 123-4567"
            value={f.customer_phone}
            onChange={updField("customer_phone")}
          />
        </div>
      </div>

      {/* Line items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">LINE ITEMS</div>
          <button
            onClick={addItem}
            data-testid="quick-legacy-add-item"
            className="text-[10px] font-mono-tech text-[#00C3FF] border border-[#00C3FF]/40 px-3 py-1.5 hover:bg-[#00C3FF]/10 uppercase tracking-wider"
          >
            <Plus size={11} className="inline mr-1" /> Row
          </button>
        </div>
        {f.items.map((it, i) => {
          const linkedProduct = (catalog || []).find((c) => c.id === it.product_id);
          const lineTotal = (Number(it.unit_price) || 0) * (Number(it.quantity) || 0);
          return (
            <div
              key={i}
              className="border border-white/10 p-3 space-y-3"
              data-testid={`quick-legacy-item-${i}`}
            >
              {/* Row 1: product selector + variant + remove */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <select
                  className="input-tech sm:col-span-7"
                  style={{ colorScheme: "dark" }}
                  value={it.product_id}
                  onChange={(e) => onPickProduct(i, e.target.value)}
                  data-testid={`quick-legacy-product-${i}`}
                >
                  <option value="">— Custom item (type below) —</option>
                  {(catalog || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <select
                  className="input-tech sm:col-span-4"
                  style={{ colorScheme: "dark" }}
                  value={it.variant_size}
                  onChange={(e) => updItem(i, "variant_size", e.target.value)}
                  disabled={!linkedProduct}
                  data-testid={`quick-legacy-variant-${i}`}
                >
                  <option value="">{linkedProduct ? "Pick size" : "—"}</option>
                  {linkedProduct?.variants?.map((v) => (
                    <option key={v.size} value={v.size}>{v.size}</option>
                  ))}
                </select>
                <button
                  onClick={() => removeItem(i)}
                  disabled={f.items.length === 1}
                  className="sm:col-span-1 flex items-center justify-center text-red-400/70 hover:text-red-300 disabled:opacity-20 border border-red-400/20 hover:border-red-400/60 py-2 sm:py-0"
                  title="Remove row"
                  data-testid={`quick-legacy-remove-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {/* Row 2: custom name (when no catalog product), unit price, qty, line total */}
              {!linkedProduct && (
                <input
                  className="input-tech w-full"
                  placeholder="Custom product name *"
                  value={it.name}
                  onChange={(e) => updItem(i, "name", e.target.value)}
                  data-testid={`quick-legacy-custom-name-${i}`}
                />
              )}
              <div className="grid grid-cols-3 gap-2 items-end">
                <div>
                  <label className="overline block mb-1">Unit $</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-tech w-full"
                    placeholder="0.00"
                    value={it.unit_price}
                    onChange={(e) => updItem(i, "unit_price", e.target.value)}
                    data-testid={`quick-legacy-unit-${i}`}
                  />
                </div>
                <div>
                  <label className="overline block mb-1">Qty</label>
                  <input
                    type="number"
                    min="1"
                    className="input-tech w-full"
                    placeholder="1"
                    value={it.quantity}
                    onChange={(e) => updItem(i, "quantity", e.target.value)}
                    data-testid={`quick-legacy-qty-${i}`}
                  />
                </div>
                <div className="text-right">
                  <div className="overline">Line</div>
                  <div className="font-mono-tech text-base text-white mt-1" data-testid={`quick-legacy-line-${i}`}>
                    ${lineTotal.toFixed(2)}
                  </div>
                </div>
              </div>
              {linkedProduct && (
                <div className="text-[9px] font-mono-tech text-[#5C657A] uppercase tracking-wider">
                  · Tier price auto-applied from catalog
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Adjustments — stacked on mobile, 2-col on sm, 5-col on lg */}
      <div>
        <div className="overline mb-2">ADJUSTMENTS</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <label className="overline block mb-1">Shipping $</label>
            <input type="number" step="0.01" className="input-tech w-full" value={f.shipping_fee} onChange={updField("shipping_fee")} placeholder="0.00" />
          </div>
          <div>
            <label className="overline block mb-1">Tax rate</label>
            <input type="number" step="0.0001" className="input-tech w-full" value={f.tax_rate} onChange={updField("tax_rate")} placeholder="0.0775" />
          </div>
          <div>
            <label className="overline block mb-1">Discount $</label>
            <input type="number" step="0.01" className="input-tech w-full" value={f.discount_amount} onChange={updField("discount_amount")} placeholder="0.00" />
          </div>
          <div>
            <label className="overline block mb-1">Force total $</label>
            <input
              data-testid="quick-legacy-target-total"
              type="number"
              step="0.01"
              className="input-tech w-full"
              value={f.target_total}
              onChange={updField("target_total")}
              placeholder="Auto"
              title="If the printed PDF total doesn't match the computed total, type the printed total here and we'll auto-adjust the discount."
            />
          </div>
          <div>
            <label className="overline block mb-1">Status</label>
            <select
              data-testid="quick-legacy-payment-status"
              className="input-tech w-full"
              style={{ colorScheme: "dark" }}
              value={f.payment_status}
              onChange={updField("payment_status")}
            >
              <option value="balance_owing">Balance Owing</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        </div>
      </div>

      {/* Total + actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3 border-t border-white/5">
        <div className="flex items-baseline gap-3">
          <div className="overline">Computed Total</div>
          <div className="font-mono-tech text-2xl text-[#00C3FF]" data-testid="quick-legacy-computed-total">
            ${computedTotal.toFixed(2)}
          </div>
        </div>
        <div className="flex gap-3 items-center justify-end">
          <button
            onClick={() => setF(empty())}
            className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider hover:text-white"
            data-testid="quick-legacy-reset"
          >
            Reset
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="quick-legacy-save"
            className="px-5 py-2.5 border border-[#00C3FF]/60 bg-[#00C3FF]/10 text-[#00C3FF] hover:bg-[#00C3FF]/20 transition-colors font-mono-tech text-[11px] uppercase tracking-[0.18em] disabled:opacity-40 flex items-center gap-2"
          >
            {busy ? <Loader2 className="animate-spin" size={13} /> : null}
            Save & Add Another
          </button>
        </div>
      </div>
    </div>
  );
}


/* ---------- Inquiries (wholesale) ---------- */
function InquiriesTab() {
  const [list, setList] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/admin/inquiries").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const approve = async (q) => {
    if (!window.confirm(`Approve ${q.email} for wholesale pricing?\n\nThis will:\n• Grant wholesale access to their account (if it exists)\n• Send them an approval email with a link to the wholesale catalog`)) return;
    setBusyId(q.id);
    try {
      const { data } = await api.post(`/admin/inquiries/${q.id}/approve-wholesale`);
      if (!data.user_found) {
        // eslint-disable-next-line no-alert
        alert(`Approved ✓\n\nNo account exists yet for ${q.email}. We've emailed them with a link to create an account, after which they'll automatically have wholesale access.`);
      }
      await load();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Approval failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-px bg-white/5">
      {list.length === 0 ? (
        <div className="bg-[#0C0C12] p-8 text-center text-[#5C657A] font-mono-tech text-sm">No inquiries yet.</div>
      ) : list.map((q) => (
        <div key={q.id} className="bg-[#0C0C12] p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="font-heading text-base">
                {q.name}
                {q.organization && <span className="text-[#5C657A] text-sm"> · {q.organization}</span>}
                {q.status === "approved" && (
                  <span className="ml-3 text-[10px] font-mono-tech text-[#00C3FF] border border-[#00C3FF]/40 px-2 py-0.5 uppercase tracking-wider" data-testid={`inquiry-status-approved-${q.id}`}>
                    ★ Approved
                  </span>
                )}
              </div>
              <div className="font-mono-tech text-xs text-[#00C3FF] mt-1">{q.email}{q.phone && ` · ${q.phone}`}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-[10px] font-mono-tech text-[#5C657A]">{new Date(q.created_at).toLocaleString()}</div>
              {q.status !== "approved" && (
                <button
                  data-testid={`inquiry-approve-wholesale-${q.id}`}
                  onClick={() => approve(q)}
                  disabled={busyId === q.id}
                  className="px-3 py-2 border border-[#00C3FF]/40 text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.18em] disabled:opacity-50"
                >
                  {busyId === q.id ? "Approving..." : "✓ Approve for Wholesale"}
                </button>
              )}
            </div>
          </div>
          {(q.quantity_estimate || q.products_of_interest) && (
            <div className="mt-3 text-xs font-mono-tech text-[#8A94A6]">
              {q.quantity_estimate && <span>Volume: {q.quantity_estimate}</span>}
              {q.quantity_estimate && q.products_of_interest && " · "}
              {q.products_of_interest && <span>Products: {q.products_of_interest}</span>}
            </div>
          )}
          <p className="text-sm text-white/80 mt-3 whitespace-pre-wrap">{q.message}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------- Messages ---------- */
function MessagesTab() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all"); // all | new | resolved

  const load = () => {
    api.get("/admin/messages").then(({ data }) => setList(data)).catch(() => setList([]));
  };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/messages/${id}`, { status });
      setList((prev) => prev.map((m) => (m.id === id ? { ...m, ...data } : m)));
    } catch (e) {
      // no-op
    }
  };

  const filtered = list.filter((m) => filter === "all" || (m.status || "new") === filter);
  const newCount = list.filter((m) => (m.status || "new") === "new").length;

  return (
    <div data-testid="admin-messages-tab">
      <div className="flex items-center gap-3 mb-4">
        {[
          { k: "all", label: `All (${list.length})` },
          { k: "new", label: `New (${newCount})` },
          { k: "resolved", label: "Resolved" },
        ].map((f) => (
          <button
            key={f.k}
            data-testid={`messages-filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-[0.18em] border transition-colors ${
              filter === f.k
                ? "border-[#00C3FF] text-[#00C3FF]"
                : "border-white/10 text-[#8A94A6] hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-px bg-white/5">
        {filtered.length === 0 ? (
          <div className="bg-[#0C0C12] p-8 text-center text-[#5C657A] font-mono-tech text-sm">
            No messages in this view.
          </div>
        ) : (
          filtered.map((m) => {
            const status = m.status || "new";
            const isResolved = status === "resolved";
            return (
              <div key={m.id} className="bg-[#0C0C12] p-5" data-testid={`message-${m.id}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <div className="font-heading text-base flex items-center gap-2">
                      {m.name}
                      <span
                        className={`text-[9px] font-mono-tech uppercase tracking-[0.18em] px-1.5 py-0.5 border ${
                          isResolved
                            ? "border-[#5C657A] text-[#5C657A]"
                            : "border-[#00C3FF] text-[#00C3FF]"
                        }`}
                      >
                        {status}
                      </span>
                    </div>
                    <div className="font-mono-tech text-xs text-[#00C3FF] mt-1">
                      <a
                        href={`mailto:${m.email}`}
                        className="hover:text-white"
                        data-testid={`message-reply-${m.id}`}
                      >
                        {m.email}
                      </a>
                      {m.order_no && (
                        <span className="text-[#5C657A]"> · order {m.order_no}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono-tech text-[#5C657A] shrink-0">
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                </div>

                <p className="text-sm text-white/80 mt-3 whitespace-pre-wrap">{m.comments}</p>

                <div className="flex items-center gap-2 mt-4">
                  {!isResolved ? (
                    <button
                      data-testid={`message-resolve-${m.id}`}
                      onClick={() => setStatus(m.id, "resolved")}
                      className="px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-[0.18em] border border-[#00C3FF] text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors"
                    >
                      Mark Resolved
                    </button>
                  ) : (
                    <button
                      data-testid={`message-reopen-${m.id}`}
                      onClick={() => setStatus(m.id, "new")}
                      className="px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-[0.18em] border border-white/15 text-[#8A94A6] hover:text-white hover:border-white/30 transition-colors"
                    >
                      Reopen
                    </button>
                  )}
                  <a
                    href={`mailto:${m.email}?subject=Re:%20Your%20message%20to%20ProSource%20Labs${
                      m.order_no ? `%20(Order%20${encodeURIComponent(m.order_no)})` : ""
                    }`}
                    className="px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-[0.18em] border border-white/15 text-[#8A94A6] hover:text-white hover:border-white/30 transition-colors"
                    data-testid={`message-reply-email-${m.id}`}
                  >
                    Reply via Email
                  </a>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ---------- Launch Waitlist ---------- */
function WaitlistTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/waitlist").then(({ data }) => setList(data)).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this subscriber from the launch waitlist?")) return;
    try {
      await api.delete(`/admin/waitlist/${id}`);
      setList((prev) => prev.filter((e) => e.id !== id));
    } catch (e) {
      // no-op
    }
  };

  const exportCsv = () => {
    const rows = [
      ["email", "source", "items_count", "created_at", "updated_at"],
      ...list.map((e) => [
        e.email,
        e.source || "",
        (e.items || []).length,
        e.created_at || "",
        e.updated_at || "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prosource-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bySource = list.reduce((acc, e) => {
    const s = e.source || "unknown";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="admin-waitlist-tab">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="overline">
            TOTAL <span className="text-[#00C3FF] ml-1">{list.length}</span>
          </div>
          {Object.entries(bySource).map(([s, c]) => (
            <div key={s} className="font-mono-tech text-[10px] text-[#8A94A6] uppercase tracking-[0.18em]">
              · {s} <span className="text-white">{c}</span>
            </div>
          ))}
        </div>
        <button
          data-testid="admin-waitlist-export"
          onClick={exportCsv}
          disabled={list.length === 0}
          className="btn-ghost text-[11px]"
          style={{ padding: "8px 14px" }}
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      <div className="space-y-px bg-white/5">
        {loading ? (
          <div className="bg-[#0C0C12] p-8 text-center text-[#5C657A] font-mono-tech text-sm">Loading…</div>
        ) : list.length === 0 ? (
          <div className="bg-[#0C0C12] p-10 text-center">
            <Mail className="mx-auto text-[#5C657A] mb-3" size={22} />
            <div className="font-mono-tech text-sm text-[#5C657A]">No one on the launch list yet.</div>
            <div className="font-mono-tech text-[10px] text-[#5C657A] uppercase tracking-[0.18em] mt-2">
              Visitors who hit "Notify Me at Launch" will appear here.
            </div>
          </div>
        ) : (
          list.map((e) => (
            <div key={e.id} className="bg-[#0C0C12] p-4 flex items-baseline justify-between gap-4">
              <div className="min-w-0">
                <div className="font-mono-tech text-sm text-[#00C3FF] truncate">{e.email}</div>
                <div className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-[0.18em] mt-1">
                  {e.source || "cart"} · {new Date(e.created_at).toLocaleString()}
                  {(e.items || []).length > 0 && ` · ${e.items.length} item${e.items.length !== 1 ? "s" : ""} saved`}
                </div>
                {(e.items || []).length > 0 && (
                  <div className="text-xs text-white/70 mt-2 font-mono-tech truncate">
                    {(e.items || []).map((i) => `${i.name || i.product_id}${i.variant ? ` · ${i.variant}` : ""}`).join(" + ")}
                  </div>
                )}
              </div>
              <button
                onClick={() => remove(e.id)}
                data-testid={`admin-waitlist-remove-${e.id}`}
                className="text-[10px] font-mono-tech text-[#5C657A] hover:text-red-400 uppercase tracking-wider flex-shrink-0"
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}



/* ---------- Profile ---------- */
function ProfileTab() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [seedMsg, setSeedMsg] = useState("");
  const [seedBusy, setSeedBusy] = useState(false);
  const [resendOrderNum, setResendOrderNum] = useState("");
  const [resendOverrideEmail, setResendOverrideEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [cleanupMsg, setCleanupMsg] = useState("");
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [binKey, setBinKey] = useState(0);

  const cleanupTestData = async () => {
    if (!window.confirm("Delete all pending (abandoned) orders + cart snapshots? Paid orders are untouched.")) return;
    setCleanupBusy(true);
    setCleanupMsg("");
    try {
      const { data } = await api.post("/admin/cleanup-test-data");
      setCleanupMsg(
        `Removed ${data.deleted_pending_orders} pending + ${data.deleted_stale_venmo_orders || 0} stale Venmo + ${data.deleted_orphan_orders} orphan orders, ${data.deleted_cart_snapshots} cart snapshots.`
      );
    } catch (e) {
      setCleanupMsg(e?.response?.data?.detail || "Cleanup failed.");
    } finally {
      setCleanupBusy(false);
    }
  };

  const previewLines = (data) => {
    const del = (data.would_delete || []).map((g) => `  · ${g.label} — ${g.customer || "?"} — $${Number(g.total || 0).toFixed(2)} — ${g.statuses.join("/")}`);
    return {
      del: del.length ? del.join("\n") : "  (none)",
      prot: (data.protected || []).length,
      count: del.length,
    };
  };

  const cleanupAllTestOrders = async () => {
    setCleanupBusy(true);
    setCleanupMsg("");
    try {
      const { data: preview } = await api.post("/admin/cleanup-all-test-orders?dry_run=true");
      const p = previewLines(preview);
      if (!p.count) {
        setCleanupMsg(`Nothing unpaid to delete. ${p.prot} paid/delivered record(s) protected.`);
        return;
      }
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        `DELETE these ${p.count} UNPAID order(s)/invoice(s)?\n\n${p.del}\n\n${p.prot} paid / processing / shipped / delivered / refunded record(s) are PROTECTED and will not be touched.\n\nDeleted items go to the Recycle Bin (restorable).`
      )) return;
      const { data } = await api.post("/admin/cleanup-all-test-orders");
      setCleanupMsg(
        `Deleted ${data.deleted_orders} unpaid order(s) + ${data.deleted_manual_invoices} unpaid invoice(s) → Recycle Bin. ${data.protected.length} protected. ${data.deleted_cart_snapshots} cart snapshots cleared.`
      );
      setBinKey((k) => k + 1);
    } catch (e) {
      setCleanupMsg(e?.response?.data?.detail || "Cleanup failed.");
    } finally {
      setCleanupBusy(false);
    }
  };

  const nukeTestData = async () => {
    try {
      const { data: preview } = await api.post("/admin/nuke-test-data?dry_run=true");
      const p = previewLines(preview);
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        `Delete test data?\n\nUNPAID orders/invoices to delete (${p.count}):\n${p.del}\n\n${p.prot} paid/delivered record(s) PROTECTED.\nAlso removes test users (test*/demo*), test messages/waitlist rows and cart snapshots.\n\nDeleted orders/invoices go to the Recycle Bin.`
      )) return;
      const { data } = await api.post("/admin/nuke-test-data");
      // eslint-disable-next-line no-alert
      alert("Test data removed:\n\n" + Object.entries(data.summary).map(([k, v]) => `  ${k}: ${v}`).join("\n"));
      window.location.reload();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Nuke failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const resendReceipt = async () => {
    setResendBusy(true);
    setResendMsg("");
    try {
      const params = resendOverrideEmail.trim() ? `?override_email=${encodeURIComponent(resendOverrideEmail.trim())}` : "";
      const { data } = await api.post(`/admin/resend-receipt/${resendOrderNum.trim()}${params}`);
      setResendMsg(`Sent to ${data.sent_to}. Operator alert to ${data.operator_alert_to}.`);
    } catch (e) {
      setResendMsg(e?.response?.data?.detail || "Failed to send receipt.");
    } finally {
      setResendBusy(false);
    }
  };

  const seedTestProduct = async () => {
    setSeedBusy(true);
    setSeedMsg("");
    try {
      const { data } = await api.post("/admin/seed-test-product");
      setSeedMsg(
        data.created
          ? "Test product created. Visit /product/admin-test-1 to test checkout."
          : "Test product already exists. Visit /product/admin-test-1 to test checkout."
      );
    } catch (e) {
      setSeedMsg(e?.response?.data?.detail || "Failed to seed test product.");
    } finally {
      setSeedBusy(false);
    }
  };

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    const wantsPasswordChange = !!form.new_password;
    if (wantsPasswordChange) {
      if (form.new_password.length < 8) {
        setMsg({ type: "error", text: "New password must be at least 8 characters." });
        return;
      }
      if (form.new_password !== form.confirm_password) {
        setMsg({ type: "error", text: "New password and confirmation do not match." });
        return;
      }
      if (!form.current_password) {
        setMsg({ type: "error", text: "Enter your current password to set a new one." });
        return;
      }
    }

    setBusy(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (wantsPasswordChange) {
        payload.current_password = form.current_password;
        payload.new_password = form.new_password;
      }
      const { data } = await api.patch("/auth/me", payload);
      setUser(data);
      setForm((s) => ({
        ...s,
        name: data.name || "",
        email: data.email || "",
        current_password: "",
        new_password: "",
        confirm_password: "",
      }));
      setMsg({ type: "ok", text: "Profile updated successfully." });
    } catch (err) {
      setMsg({
        type: "error",
        text: err?.response?.data?.detail || "Unable to update profile. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-profile-tab">
      <h2 className="font-heading text-2xl text-white">Your Admin Profile</h2>
      <p className="text-sm text-[#8A94A6] mt-2">
        Update the email and password used to sign in to this Admin Console. Leave the password
        fields blank to keep your current password.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div>
          <label className="overline block mb-2">Display Name</label>
          <input
            data-testid="profile-name-input"
            className="input-tech"
            value={form.name}
            onChange={upd("name")}
            placeholder="ProSource Admin"
          />
        </div>

        <div>
          <label className="overline block mb-2">Email</label>
          <input
            data-testid="profile-email-input"
            type="email"
            required
            className="input-tech"
            value={form.email}
            onChange={upd("email")}
          />
        </div>

        <div className="pt-6 border-t border-white/10">
          <div className="overline mb-4">Change Password</div>

          <div className="space-y-6">
            <div>
              <label className="overline block mb-2">Current Password</label>
              <input
                data-testid="profile-current-pw-input"
                type="password"
                className="input-tech"
                value={form.current_password}
                onChange={upd("current_password")}
                placeholder="Required to change password"
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="overline block mb-2">New Password</label>
              <input
                data-testid="profile-new-pw-input"
                type="password"
                minLength={8}
                className="input-tech"
                value={form.new_password}
                onChange={upd("new_password")}
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="overline block mb-2">Confirm New Password</label>
              <input
                data-testid="profile-confirm-pw-input"
                type="password"
                minLength={8}
                className="input-tech"
                value={form.confirm_password}
                onChange={upd("confirm_password")}
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>

        {msg.text && (
          <div
            data-testid={msg.type === "ok" ? "profile-success" : "profile-error"}
            className={`text-xs font-mono-tech pl-3 border-l-2 ${
              msg.type === "ok"
                ? "text-[#00C3FF] border-[#00C3FF]"
                : "text-red-400 border-red-400"
            }`}
          >
            {msg.text}
          </div>
        )}

        <button
          data-testid="profile-save-button"
          type="submit"
          disabled={busy}
          className="btn-primary"
        >
          {busy ? "Saving..." : "Save Changes"}
        </button>
      </form>

      <div className="mt-12 pt-8 border-t border-white/10">
        <div className="overline mb-3">SESSION</div>
        <p className="text-sm text-[#8A94A6] mb-4">
          End your admin session on this device. You'll be signed out and returned to the homepage.
        </p>
        <button
          data-testid="profile-signout-button"
          onClick={() => { logout(); navigate("/"); }}
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-[#8A94A6] hover:text-[#00C3FF] hover:border-[#00C3FF] transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em]"
        >
          <LogOut size={12} />
          Sign Out of Admin
        </button>
      </div>

      <div className="mt-12 pt-8 border-t border-white/10">
        <div className="overline mb-3">PAYMENT TESTING</div>
        <p className="text-sm text-[#8A94A6] mb-4">
          Create a hidden $1.00 product for live PayPal/Venmo verification. Safe to call multiple times.
          After your test, delete the product via Admin → Products.
        </p>
        <button
          data-testid="profile-seed-test-product"
          onClick={seedTestProduct}
          disabled={seedBusy}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#00C3FF]/40 text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
        >
          {seedBusy ? "Working..." : "Create $1 Admin Test Product"}
        </button>
        {seedMsg && (
          <div
            data-testid="profile-seed-msg"
            className="mt-3 text-xs text-[#8A94A6] font-mono-tech"
          >
            {seedMsg}
          </div>
        )}

        <div className="mt-6">
          <p className="text-sm text-[#8A94A6] mb-3">
            Re-send the confirmation receipt for an existing paid order (e.g., if the email landed in spam
            or didn't arrive at all). Enter the order number, e.g. PSL-0006.
          </p>
          <div className="flex gap-2 flex-wrap">
            <input
              data-testid="profile-resend-order-input"
              value={resendOrderNum}
              onChange={(e) => setResendOrderNum(e.target.value.toUpperCase())}
              placeholder="PSL-0006"
              className="input-tech text-sm flex-1 min-w-[140px]"
            />
            <input
              data-testid="profile-resend-override-email"
              value={resendOverrideEmail}
              onChange={(e) => setResendOverrideEmail(e.target.value)}
              placeholder="override email (optional)"
              className="input-tech text-sm flex-1 min-w-[180px]"
            />
            <button
              data-testid="profile-resend-receipt-button"
              onClick={resendReceipt}
              disabled={resendBusy || !resendOrderNum.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#00C3FF]/40 text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
            >
              {resendBusy ? "Sending..." : "Resend Receipt"}
            </button>
          </div>
          {resendMsg && (
            <div
              data-testid="profile-resend-msg"
              className="mt-3 text-xs text-[#8A94A6] font-mono-tech"
            >
              {resendMsg}
            </div>
          )}
        </div>

        <div className="mt-6">
          <p className="text-sm text-[#8A94A6] mb-3">
            Delete UNPAID orders/invoices only (pending, awaiting payment, awaiting Venmo, cancelled). You'll see the exact list before anything is deleted, and deleted items go to the Recycle Bin.
            <span className="text-white"> Paid / processing / shipped / delivered / refunded orders and invoices are never touched.</span>
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="profile-cleanup-test-data"
              onClick={cleanupTestData}
              disabled={cleanupBusy}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-400/30 text-red-300 hover:bg-red-500/10 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
            >
              {cleanupBusy ? "Cleaning..." : "Cleanup Abandoned (>7 days)"}
            </button>
            <button
              data-testid="profile-cleanup-all-test-orders"
              onClick={cleanupAllTestOrders}
              disabled={cleanupBusy}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/60 text-red-200 bg-red-500/10 hover:bg-red-500/20 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
            >
              {cleanupBusy ? "Nuking..." : "☢ Nuke Unpaid Orders + Invoices"}
            </button>
            <button
              data-testid="profile-nuke-test-data"
              onClick={nukeTestData}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-600 text-red-100 bg-red-600/20 hover:bg-red-600/40 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em]"
            >
              ☢ Nuke Unpaid Test Data (orders + invoices + test users)
            </button>
            <button
              data-testid="profile-sync-wholesale-prices"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/sync-wholesale-prices");
                  const lines = [`Updated ${data.updated_count} variant(s):`];
                  for (const u of (data.updated || [])) {
                    lines.push(`  · ${u.name} ${u.size} (${u.sku}) → $${u.wholesale_price}/vial`);
                  }
                  if ((data.unmatched || []).length) {
                    lines.push("", "SKUs with no pricelist entry (not in wholesale map):");
                    for (const u of data.unmatched) {
                      lines.push(`  · ${u.name} ${u.size} (${u.sku})`);
                    }
                  }
                  // eslint-disable-next-line no-alert
                  alert(lines.join("\n"));
                } catch (e) {
                  // eslint-disable-next-line no-alert
                  alert("Sync failed: " + (e?.response?.data?.detail || e.message));
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#00C3FF] text-[#00C3FF] bg-[#00C3FF]/10 hover:bg-[#00C3FF]/20 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.22em]"
            >
              ⟳ Sync Wholesale Pricelist → Catalog
            </button>
          </div>
          {cleanupMsg && (
            <div
              data-testid="profile-cleanup-msg"
              className="mt-3 text-xs text-[#8A94A6] font-mono-tech"
            >
              {cleanupMsg}
            </div>
          )}
        </div>
        <RecycleBin key={binKey} />
      </div>
    </div>
  );
}


/* ---------- Blog (SEO Research Articles) ---------- */
function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [gen, setGen] = useState({ topic: "", target_keyword: "", linked_product_slug: "", tone: "educational", word_count: 1600 });
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const loadPosts = () =>
    api.get("/admin/blog/posts").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  useEffect(() => {
    loadPosts();
    api.get("/products").then(({ data }) => setCatalog(data || [])).catch(() => setCatalog([]));
  }, []);

  const generateArticle = async () => {
    if (!gen.topic.trim()) {
      // eslint-disable-next-line no-alert
      alert("Topic is required.");
      return;
    }
    setGenerating(true);
    setDraft(null);
    try {
      const { data } = await api.post("/admin/blog/generate", gen, { timeout: 120000 });
      setDraft({ ...data, cover_image: "", published: true });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const savePost = async () => {
    if (!draft) return;
    // Compliance guard — force explicit confirmation if there are still banned terms.
    if (draft.compliance_warnings && draft.compliance_warnings.length && draft.published) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        `⚠️ COMPLIANCE WARNING\n\nThis draft still contains ${draft.compliance_warnings.length} banned term(s):\n\n· ${draft.compliance_warnings.join("\n· ")}\n\nPublishing as-is could create legal liability for research peptide sales.\n\nRecommendation: click Cancel, edit the article to remove these terms, then Save.\n\nProceed with publishing anyway?`
      )) return;
    }
    setSaving(true);
    try {
      await api.post("/admin/blog/posts", {
        slug: draft.slug,
        title: draft.title,
        excerpt: draft.excerpt,
        body_markdown: draft.body_markdown,
        cover_image: draft.cover_image || null,
        tags: draft.tags || [],
        published: !!draft.published,
      });
      setDraft(null);
      setGen({ topic: "", target_keyword: "", linked_product_slug: "", tone: "educational", word_count: 1600 });
      loadPosts();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const editPost = async (slug) => {
    try {
      const { data } = await api.get(`/admin/blog/posts/${slug}`);
      // Compute compliance warnings so the banner appears if needed.
      const banned = ["reconstitut","dosing","dosage","mg/kg","administer","administration","inject","subcutaneous","intramuscular","oral administration","sublingual","cycle length","cycling","stacking with","protocol","how to use","how to take","take orally"];
      const lower = (data.body_markdown || "").toLowerCase();
      const found = banned.filter((t) => lower.includes(t));
      setDraft({
        slug: data.slug,
        title: data.title,
        excerpt: data.excerpt,
        body_markdown: data.body_markdown,
        cover_image: data.cover_image || "",
        tags: data.tags || [],
        published: !!data.published,
        compliance_warnings: found,
      });
      // Scroll to the draft editor
      setTimeout(() => {
        document.querySelector('[data-testid="admin-blog-draft"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Load failed");
    }
  };

  const togglePublish = async (slug) => {
    try {
      const { data } = await api.post(`/admin/blog/posts/${slug}/toggle-publish`);
      loadPosts();
      return data.published;
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Toggle failed");
      return null;
    }
  };

  const deletePost = async (slug) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete research article "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/posts/${slug}`);
      loadPosts();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const seedStarterArticles = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      "Generate the 10 starter articles?\n\nThis runs in the background — takes 5-15 minutes total and consumes LLM credits. Compliance-safe topics only. Idempotent: articles that already exist are skipped. Articles with banned terms are auto-saved as drafts.\n\nProceed?"
    )) return;
    setSeeding(true);
    try {
      const { data: kick } = await api.post("/admin/blog/seed-starter", {}, { timeout: 30000 });
      const taskId = kick.task_id;
      const total = kick.total;

      // Poll status every 8 seconds. Refresh the posts list each poll so new
      // articles appear in the "Published & Drafts" panel as they land.
      const startedAt = Date.now();
      const MAX_WAIT_MS = 20 * 60 * 1000; // 20 min hard cap
      while (Date.now() - startedAt < MAX_WAIT_MS) {
        await new Promise((r) => setTimeout(r, 8000));
        // eslint-disable-next-line no-await-in-loop
        const { data: state } = await api.get(`/admin/blog/seed-starter/status/${taskId}`);
        // eslint-disable-next-line no-await-in-loop
        await loadPosts();
        if (state.status === "done") {
          const pub = state.generated.filter((g) => g.published).length;
          const draft = state.generated.filter((g) => !g.published).length;
          const lines = [
            `✓ Published: ${pub} (compliance-clean)`,
            `📝 Auto-drafted: ${draft} (had banned terms — review before publishing)`,
            `↷ Skipped (already exist): ${state.skipped.length}`,
            `✗ Failed: ${state.failed.length}`,
          ];
          if (draft > 0) {
            lines.push("", "Drafts needing review:");
            state.generated.filter((g) => !g.published).forEach((w) => {
              lines.push(`  · /${w.slug} → ${w.compliance_warnings.join(", ")}`);
            });
          }
          if (state.failed.length > 0) {
            lines.push("", "Failed topics (usually LLM budget):");
            state.failed.forEach((f) => lines.push(`  · ${f.topic}: ${(f.error || "").slice(0, 80)}`));
          }
          // eslint-disable-next-line no-alert
          alert(lines.join("\n"));
          return;
        }
        // Still running — update button label to show progress
        // (state.completed / state.total)
        if (state.completed !== undefined) {
          document.title = `Seeding ${state.completed}/${total}… · ProSource Admin`;
        }
      }
      // eslint-disable-next-line no-alert
      alert("Seed still running after 20 min — check the Research Library list to see what landed. It'll keep running in the background.");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Seed failed — check backend logs");
    } finally {
      setSeeding(false);
      document.title = "ProSource Admin";
    }
  };

  return (
    <div className="space-y-6">
      {/* Generator */}
      <div className="surface p-5 sm:p-6 space-y-4" data-testid="admin-blog-generator">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline">AI ARTICLE GENERATOR</div>
            <p className="text-xs text-[#8A94A6] mt-1">
              Uses Claude Sonnet 4.6 to draft an SEO-optimized research article. Review the draft, edit if needed, then Save to publish.
            </p>
          </div>
          <button
            onClick={seedStarterArticles}
            disabled={seeding}
            data-testid="admin-blog-seed-starter"
            title="One-click generate 20 compliance-safe starter articles across your top peptide categories. Idempotent — safely skips any that already exist."
            className="shrink-0 px-4 py-2 border border-purple-400/50 bg-purple-400/10 text-purple-300 hover:bg-purple-400/20 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.18em] disabled:opacity-40 flex items-center gap-2"
          >
            {seeding ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />}
            {seeding ? "Seeding… (10-20 min)" : "Seed 20 Starter Articles"}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <label className="overline block mb-1">Article topic *</label>
            <input
              className="input-tech w-full"
              placeholder="e.g. BPC-157 mechanism of action"
              value={gen.topic}
              onChange={(e) => setGen({ ...gen, topic: e.target.value })}
              data-testid="admin-blog-topic"
            />
          </div>
          <div>
            <label className="overline block mb-1">Target keyword</label>
            <input
              className="input-tech w-full"
              placeholder="e.g. BPC-157 half life"
              value={gen.target_keyword}
              onChange={(e) => setGen({ ...gen, target_keyword: e.target.value })}
              data-testid="admin-blog-keyword"
            />
          </div>
          <div>
            <label className="overline block mb-1">Word count</label>
            <input
              type="number"
              min="800"
              max="3000"
              className="input-tech w-full"
              value={gen.word_count}
              onChange={(e) => setGen({ ...gen, word_count: Number(e.target.value) })}
              data-testid="admin-blog-wordcount"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Link to product (optional — 1 internal link added)</label>
            <select
              className="input-tech w-full"
              style={{ colorScheme: "dark" }}
              value={gen.linked_product_slug}
              onChange={(e) => setGen({ ...gen, linked_product_slug: e.target.value })}
              data-testid="admin-blog-product"
            >
              <option value="">— None —</option>
              {catalog.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Tone</label>
            <select
              className="input-tech w-full"
              style={{ colorScheme: "dark" }}
              value={gen.tone}
              onChange={(e) => setGen({ ...gen, tone: e.target.value })}
              data-testid="admin-blog-tone"
            >
              <option value="clinical">Strictly clinical / research-paper</option>
              <option value="educational">Educational, accessible</option>
              <option value="mixed">Clinical with TL;DR summary</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <button
            onClick={generateArticle}
            disabled={generating}
            data-testid="admin-blog-generate"
            className="px-5 py-2.5 border border-[#00C3FF]/60 bg-[#00C3FF]/10 text-[#00C3FF] hover:bg-[#00C3FF]/20 transition-colors font-mono-tech text-[11px] uppercase tracking-[0.18em] disabled:opacity-40 flex items-center gap-2"
          >
            {generating ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />}
            {generating ? "Generating… (30-60s)" : "Generate Draft Article"}
          </button>
        </div>
      </div>

      {/* Draft editor */}
      {draft && (
        <div className="surface p-5 sm:p-6 space-y-4" data-testid="admin-blog-draft">
          <div className="flex items-center justify-between">
            <div className="overline">DRAFT · REVIEW &amp; SAVE</div>
            <button
              onClick={() => setDraft(null)}
              className="text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider hover:text-red-300"
            >
              Discard
            </button>
          </div>

          {/* Compliance warnings — critical for research peptide legality */}
          {(draft.compliance_warnings && draft.compliance_warnings.length > 0) && (
            <div
              className="border border-red-400/60 bg-red-500/10 p-4 space-y-2"
              data-testid="admin-blog-compliance-warning"
            >
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-red-300 flex items-center gap-2">
                <AlertTriangle size={13} /> COMPLIANCE REVIEW REQUIRED · DO NOT PUBLISH AS-IS
              </div>
              <p className="text-xs text-red-200/90">
                The AI draft contains banned terms that could create legal liability for research peptide sales. Edit the article body below to remove/rephrase all mentions of:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {draft.compliance_warnings.map((w) => (
                  <span key={w} className="text-[10px] font-mono-tech text-red-300 bg-red-500/10 border border-red-400/40 px-2 py-0.5 uppercase tracking-wider">
                    {w}
                  </span>
                ))}
              </div>
              <p className="text-[10px] font-mono-tech text-red-300/70 uppercase tracking-wider">
                Use Ctrl+F in the body editor to find each instance and rewrite.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Title</label>
              <input className="input-tech w-full" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            </div>
            <div>
              <label className="overline block mb-1">Slug (URL)</label>
              <input className="input-tech w-full" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
            </div>
          </div>

          <div>
            <label className="overline block mb-1">Excerpt (SEO meta description)</label>
            <textarea className="input-tech w-full" rows={2} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} />
          </div>

          <div>
            <label className="overline block mb-1">Cover image URL (optional)</label>
            <input className="input-tech w-full" placeholder="https://…" value={draft.cover_image || ""} onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })} />
          </div>

          <div>
            <label className="overline block mb-1">Article body (markdown)</label>
            <textarea
              className="input-tech w-full font-mono text-xs"
              rows={22}
              value={draft.body_markdown}
              onChange={(e) => {
                const body = e.target.value;
                const lower = body.toLowerCase();
                const banned = ["reconstitut","dosing","dosage","mg/kg","administer","administration","inject","subcutaneous","intramuscular","oral administration","sublingual","cycle length","cycling","stacking with","protocol","how to use","how to take","take orally"];
                const found = banned.filter(t => lower.includes(t));
                setDraft({ ...draft, body_markdown: body, compliance_warnings: found });
              }}
              data-testid="admin-blog-draft-body"
            />
            <div className="text-[10px] font-mono-tech text-[#5C657A] mt-1">
              {(draft.body_markdown || "").split(/\s+/).filter(Boolean).length} words · Preview renders on /research-library/{draft.slug} after save
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <label className="text-xs text-[#8A94A6] flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!draft.published}
                onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
              />
              Publish immediately
            </label>
            <button
              onClick={savePost}
              disabled={saving}
              data-testid="admin-blog-save"
              className="px-5 py-2.5 border border-green-400/60 bg-green-400/10 text-green-300 hover:bg-green-400/20 transition-colors font-mono-tech text-[11px] uppercase tracking-[0.18em] disabled:opacity-40 flex items-center gap-2"
            >
              {saving ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />}
              {draft.published ? "Save & Publish" : "Save as Draft"}
            </button>
          </div>
        </div>
      )}

      {/* Existing posts list */}
      <div className="surface p-5 sm:p-6">
        <div className="overline mb-4">PUBLISHED &amp; DRAFTS ({posts.length})</div>
        {posts.length === 0 ? (
          <div className="text-[#5C657A] font-mono-tech text-sm">No research articles yet. Generate your first one above.</div>
        ) : (
          <div className="space-y-2" data-testid="admin-blog-list">
            {posts.map((p) => (
              <div
                key={p.slug}
                className="border border-white/10 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                data-testid={`admin-blog-row-${p.slug}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-tech text-[10px] uppercase tracking-wider text-[#00C3FF]">/research-library/{p.slug}</span>
                    {!p.published && <span className="font-mono-tech text-[9px] text-yellow-300/80 uppercase tracking-wider">DRAFT</span>}
                    <span className="font-mono-tech text-[9px] text-[#5C657A]">· {p.view_count || 0} views</span>
                  </div>
                  <div className="text-sm text-white truncate mt-0.5">{p.title}</div>
                  <div className="text-xs text-[#8A94A6] truncate">{p.excerpt}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`/research-library/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] font-mono-tech text-[#00C3FF] hover:underline uppercase tracking-wider"
                  >
                    View →
                  </a>
                  <button
                    onClick={() => editPost(p.slug)}
                    data-testid={`admin-blog-edit-${p.slug}`}
                    title="Load into editor to review + republish"
                    className="text-white/60 hover:text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors p-2 border border-white/10 hover:border-[#00C3FF]/60"
                  >
                    <Edit3 size={13} />
                  </button>
                  <button
                    onClick={() => togglePublish(p.slug)}
                    data-testid={`admin-blog-toggle-${p.slug}`}
                    title={p.published ? "Unpublish (move to draft)" : "Publish now"}
                    className={`p-2 border transition-colors ${p.published
                      ? "border-yellow-400/30 text-yellow-300/70 hover:bg-yellow-400/10 hover:border-yellow-400/60"
                      : "border-green-400/40 text-green-300 hover:bg-green-400/10 hover:border-green-400/70"
                    }`}
                  >
                    <span className="text-[9px] font-mono-tech uppercase tracking-wider px-1">
                      {p.published ? "Unpub" : "Publish"}
                    </span>
                  </button>
                  <button
                    onClick={() => deletePost(p.slug)}
                    className="text-red-400/70 hover:text-red-300 hover:bg-red-400/10 transition-colors p-2 border border-red-400/20 hover:border-red-400/60"
                    data-testid={`admin-blog-delete-${p.slug}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ============================================================ */
/*  CAMPAIGNS TAB — welcome blast + weekly recurring flow        */
/* ============================================================ */
function CampaignsTab() {
  const [settings, setSettings] = useState(null);
  const [audience, setAudience] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [addr, setAddr] = useState("");
  const [blastTask, setBlastTask] = useState(null);
  const [blastLoading, setBlastLoading] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    const [s, a, c] = await Promise.all([
      api.get("/admin/campaigns/settings"),
      api.get("/admin/campaigns/audience"),
      api.get("/admin/campaigns"),
    ]);
    setSettings(s.data);
    setAddr(s.data.mailing_address || "");
    setAudience(a.data);
    setCampaigns(c.data);
  };

  useEffect(() => { load().catch(console.error); }, []);

  // Poll blast task progress
  useEffect(() => {
    if (!blastTask?.task_id || blastTask.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/campaigns/welcome-blast/status/${blastTask.task_id}`);
        setBlastTask(data);
        if (data.status !== "running") { clearInterval(t); load(); }
      } catch { clearInterval(t); }
    }, 3000);
    return () => clearInterval(t);
  }, [blastTask?.task_id, blastTask?.status]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/campaigns/settings", { mailing_address: addr });
      setSettings(data);
      alert("Mailing address saved.");
    } catch (e) {
      alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSavingSettings(false); }
  };

  const fireWelcomeBlast = async () => {
    if (!settings?.mailing_address) return alert("Please set the mailing address first.");
    const total = (audience?.existing_customers || 0) + (audience?.new_captured_emails || 0);
    if (!window.confirm(
      `Send the welcome blast to ${total} recipients now?\n\n` +
      `• ${audience.new_captured_emails} new customers → WELCOME10 (10% off first order)\n` +
      `• ${audience.existing_customers} existing customers → THANKS10 (10% off next order)\n\n` +
      `From: support@prosourcelabs.org\nEstimated send time: ~${Math.ceil(total * 0.22 / 60)} min`
    )) return;
    setBlastLoading(true);
    try {
      const { data } = await api.post("/admin/campaigns/welcome-blast?confirm=SEND_NOW");
      setBlastTask(data);
    } catch (e) {
      alert(`Blast failed to start: ${e?.response?.data?.detail || e.message}`);
    } finally { setBlastLoading(false); }
  };

  const canBlast = !!settings?.mailing_address;
  const runningBlast = blastTask?.status === "running";

  return (
    <div className="space-y-8" data-testid="campaigns-tab">
      <NewCustomerOfferPanel />
      {/* ------- Settings ------- */}
      <section className="border border-white/10 bg-[#0C0C12] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={16} className="text-[#00C3FF]" />
          <div className="overline">Campaign Settings</div>
        </div>
        <p className="text-sm text-[#8A94A6] mb-4">
          Required by CAN-SPAM. Every marketing email must include a physical mailing address
          in the footer. Sends from <span className="font-mono text-[#00C3FF]">support@prosourcelabs.org</span>.
        </p>
        <label className="block text-xs uppercase tracking-[0.2em] text-[#5C657A] font-mono mb-2">Physical mailing address</label>
        <input
          type="text"
          value={addr}
          onChange={(e) => setAddr(e.target.value)}
          placeholder="e.g. ProSource Labs, 123 Main St Ste 400, Los Angeles, CA 90001"
          className="w-full bg-[#05050A] border border-white/10 focus:border-[#00C3FF] px-4 py-2.5 text-white text-sm font-mono outline-none"
          data-testid="campaigns-mailing-address-input"
        />
        <button
          onClick={saveSettings}
          disabled={savingSettings || !addr.trim()}
          className="mt-3 px-4 py-2 border border-[#00C3FF]/40 bg-[#00C3FF]/10 text-[#00C3FF] hover:bg-[#00C3FF]/20 font-mono-tech text-[10px] uppercase tracking-[0.18em] disabled:opacity-40 flex items-center gap-2"
          data-testid="campaigns-save-settings-btn"
        >
          {savingSettings ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
          Save Address
        </button>
      </section>

      {/* ------- Audience ------- */}
      {audience && (
        <section className="border border-white/10 bg-[#0C0C12] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-[#00C3FF]" />
            <div className="overline">Audience Snapshot</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <AudienceStat label="Existing Customers" value={audience.existing_customers} testId="aud-existing" />
            <AudienceStat label="New / Captured" value={audience.new_captured_emails} testId="aud-new" />
            <AudienceStat label="Total Reachable" value={audience.total_reachable} testId="aud-total" highlight />
            <AudienceStat label="Unsubscribed" value={audience.unsubscribed} testId="aud-unsub" muted />
          </div>
        </section>
      )}

      {/* ------- Welcome / Thanks Blast ------- */}
      <section className="border border-white/10 bg-[#0C0C12] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Send size={16} className="text-[#00C3FF]" />
          <div className="overline">Welcome + Thank-You Blast</div>
        </div>
        <p className="text-sm text-[#8A94A6] mb-4">
          One-time blast sent to <strong>every reachable email</strong>. New/uncaptured
          customers get <span className="font-mono text-[#00C3FF]">WELCOME10</span> (10% off first order),
          existing customers get <span className="font-mono text-[#00C3FF]">THANKS10</span> (10% off next order).
        </p>

        {/* Preview buttons — open the exact HTML the recipient will see, in a new tab */}
        <div className="flex flex-wrap gap-2 mb-4">
          <a
            href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/admin/campaigns/preview-email?kind=welcome`}
            target="_blank" rel="noopener noreferrer"
            className="px-3 py-2 border border-white/20 text-white hover:bg-white/5 font-mono-tech text-[10px] uppercase tracking-[0.18em]"
            data-testid="preview-welcome-btn"
          >
            👁 Preview Welcome Email
          </a>
          <a
            href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/admin/campaigns/preview-email?kind=thanks`}
            target="_blank" rel="noopener noreferrer"
            className="px-3 py-2 border border-white/20 text-white hover:bg-white/5 font-mono-tech text-[10px] uppercase tracking-[0.18em]"
            data-testid="preview-thanks-btn"
          >
            👁 Preview Thanks Email
          </a>
        </div>

        {runningBlast ? (
          <div className="border border-[#00C3FF]/40 bg-[#00C3FF]/5 p-4">
            <div className="flex items-center gap-2 text-[#00C3FF] font-mono text-xs uppercase tracking-wider">
              <Loader2 className="animate-spin" size={14} />
              Sending… {blastTask.completed}/{blastTask.total}
            </div>
            <div className="mt-2 text-sm text-[#8A94A6]">
              Sent: {blastTask.sent} · Failed: {blastTask.failed}
            </div>
            <div className="mt-3 h-1 bg-white/5">
              <div className="h-1 bg-[#00C3FF]" style={{ width: `${blastTask.total ? (blastTask.completed / blastTask.total * 100) : 0}%` }} />
            </div>
          </div>
        ) : blastTask?.status === "done" ? (
          <div className="border border-emerald-400/40 bg-emerald-400/5 p-4 text-sm">
            <div className="text-emerald-300 font-mono text-xs uppercase tracking-wider mb-1">Blast Complete</div>
            Sent {blastTask.sent} of {blastTask.total} · {blastTask.failed} failed
          </div>
        ) : (
          <button
            onClick={fireWelcomeBlast}
            disabled={!canBlast || blastLoading}
            className="px-5 py-2.5 bg-[#00C3FF] text-black font-mono-tech text-[11px] uppercase tracking-[0.2em] hover:bg-[#00C3FF]/90 disabled:opacity-40 flex items-center gap-2"
            data-testid="fire-welcome-blast-btn"
          >
            {blastLoading ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
            Send Welcome Blast Now
          </button>
        )}
        {!canBlast && (
          <div className="mt-3 flex items-start gap-2 text-xs text-amber-300">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            Save a mailing address above before sending. Required by CAN-SPAM law.
          </div>
        )}
      </section>

      {/* ------- Weekly Recurring Campaigns ------- */}
      <section className="border border-white/10 bg-[#0C0C12] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar size={16} className="text-[#00C3FF]" />
              <div className="overline">Weekly Recurring Campaigns</div>
            </div>
            <p className="text-sm text-[#8A94A6]">
              AI-drafted weekly emails staggered <strong>Thu / Fri at 10 AM PT</strong> (even ISO weeks → Thu, odd → Fri).
              You approve & edit every draft before scheduling.
            </p>
          </div>
          <button
            onClick={() => { setDraft({ theme: "", featured_product_slug: "", tone: "educational" }); setShowDraftModal(true); }}
            className="shrink-0 px-4 py-2 border border-purple-400/50 bg-purple-400/10 text-purple-300 hover:bg-purple-400/20 font-mono-tech text-[10px] uppercase tracking-[0.18em] flex items-center gap-2"
            data-testid="new-weekly-draft-btn"
          >
            <Sparkles size={12} /> New AI Draft
          </button>
        </div>

        {campaigns.length === 0 ? (
          <div className="text-[#5C657A] font-mono-tech text-sm">No campaigns yet. Click &ldquo;New AI Draft&rdquo; to generate your first.</div>
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <CampaignRow key={c.id} campaign={c} reload={load} onEdit={(d) => { setDraft(d); setShowDraftModal(true); }} />
            ))}
          </div>
        )}
      </section>

      {showDraftModal && draft && (
        <WeeklyDraftModal
          draft={draft}
          onClose={() => { setShowDraftModal(false); setDraft(null); }}
          onSaved={async () => { setShowDraftModal(false); setDraft(null); await load(); }}
        />
      )}
    </div>
  );
}

function AudienceStat({ label, value, testId, highlight, muted }) {
  return (
    <div className={`border ${highlight ? "border-[#00C3FF]/40 bg-[#00C3FF]/5" : "border-white/10 bg-[#05050A]"} p-4`} data-testid={testId}>
      <div className={`text-[10px] font-mono uppercase tracking-[0.18em] ${muted ? "text-[#5C657A]" : "text-[#8A94A6]"}`}>{label}</div>
      <div className={`font-heading text-2xl mt-1 ${highlight ? "text-[#00C3FF]" : muted ? "text-[#5C657A]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function CampaignRow({ campaign, reload, onEdit }) {
  const [loading, setLoading] = useState(false);
  const statusColor = {
    draft: "text-[#8A94A6]",
    scheduled: "text-purple-300",
    sending: "text-[#00C3FF]",
    sent: "text-emerald-300",
  }[campaign.status] || "text-white";

  const doAction = async (fn) => {
    setLoading(true);
    try { await fn(); await reload(); }
    catch (e) { alert(`Failed: ${e?.response?.data?.detail || e.message}`); }
    finally { setLoading(false); }
  };

  const loadFull = async () => {
    const { data } = await api.get(`/admin/campaigns/${campaign.id}`);
    onEdit({ ...data });
  };

  return (
    <div className="border border-white/10 p-4 flex items-start justify-between gap-4" data-testid={`campaign-row-${campaign.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`font-mono text-[10px] uppercase tracking-wider ${statusColor}`}>{campaign.status}</span>
          {campaign.scheduled_for && <span className="text-[10px] text-purple-300 font-mono">→ {new Date(campaign.scheduled_for).toLocaleString()}</span>}
          {campaign.sent_at && <span className="text-[10px] text-emerald-300 font-mono">sent {new Date(campaign.sent_at).toLocaleString()} · {campaign.sent_count || 0}/{campaign.recipients_count || 0}</span>}
        </div>
        <div className="text-white text-sm font-medium truncate">{campaign.subject}</div>
        {campaign.theme && <div className="text-xs text-[#5C657A] mt-1">Theme: {campaign.theme}</div>}
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {campaign.status === "draft" && (
          <>
            <button onClick={loadFull} className="px-3 py-1.5 border border-white/20 text-white text-[10px] font-mono uppercase tracking-wider hover:bg-white/5">Edit</button>
            <button onClick={() => doAction(() => api.post(`/admin/campaigns/${campaign.id}/schedule`))} disabled={loading} className="px-3 py-1.5 border border-purple-400/50 text-purple-300 text-[10px] font-mono uppercase tracking-wider hover:bg-purple-400/10" data-testid={`schedule-${campaign.id}`}>Schedule</button>
            <button onClick={() => { if (window.confirm("Send this campaign to ALL reachable emails right now?")) doAction(() => api.post(`/admin/campaigns/${campaign.id}/send-now`)); }} disabled={loading} className="px-3 py-1.5 bg-[#00C3FF] text-black text-[10px] font-mono uppercase tracking-wider" data-testid={`sendnow-${campaign.id}`}>Send Now</button>
            <button onClick={() => { if (window.confirm("Delete this draft?")) doAction(() => api.delete(`/admin/campaigns/${campaign.id}`)); }} disabled={loading} className="p-1.5 text-red-400 hover:bg-red-400/10"><Trash2 size={12} /></button>
          </>
        )}
        {campaign.status === "scheduled" && (
          <button onClick={() => { if (window.confirm("Cancel schedule (moves back to draft)?")) doAction(() => api.delete(`/admin/campaigns/${campaign.id}`)); }} disabled={loading} className="px-3 py-1.5 border border-white/20 text-white text-[10px] font-mono uppercase tracking-wider">Cancel</button>
        )}
      </div>
    </div>
  );
}

function WeeklyDraftModal({ draft, onClose, onSaved }) {
  const [state, setState] = useState({
    id: draft.id || null,
    theme: draft.theme || "",
    featured_product_slug: draft.featured_product_slug || "",
    tone: draft.tone || "educational",
    subject: draft.subject || "",
    preview_text: draft.preview_text || "",
    body_html: draft.body_html || "",
    product_discount_percent: draft.product_discount_percent || "",
    product_discount_code: draft.product_discount_code || "",
    description: draft.description || "",
    effects: draft.effects || [],
    compliance_warnings: draft.compliance_warnings || [],
    // NEW · Custom hero image + custom schedule. Both default off/empty so
    // existing campaign workflow is unchanged when admin doesn't opt in.
    hero_image_url: draft.hero_image_url || "",
    use_custom_schedule: false,
    custom_scheduled_for: "",
  });
  const [effectsCatalog, setEffectsCatalog] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]);
  useEffect(() => {
    api.get("/admin/campaigns/effects-catalog").then(({ data }) => setEffectsCatalog(data?.effects || [])).catch(() => setEffectsCatalog([]));
    // Load live product catalog for the featured-product picker so the admin
    // never has to type a slug by hand (typos + trailing spaces were silently
    // breaking hero card rendering — bug 2026-02-12).
    api.get("/products").then(({ data }) => setProductCatalog(data || [])).catch(() => setProductCatalog([]));
  }, []);

  // Featured products are stored as a comma-separated slug string (backend
  // contract). Expose an array to the UI for cleaner toggling.
  const selectedSlugs = (state.featured_product_slug || "")
    .split(",").map((s) => s.trim()).filter(Boolean);
  const toggleFeaturedProduct = (slug) => {
    setState((s) => {
      const current = (s.featured_product_slug || "").split(",").map((x) => x.trim()).filter(Boolean);
      const has = current.includes(slug);
      let next;
      if (has) {
        next = current.filter((x) => x !== slug);
      } else if (current.length >= 3) {
        alert("Up to 3 products can be featured in one campaign. Deselect one first.");
        return s;
      } else {
        next = [...current, slug];
      }
      return { ...s, featured_product_slug: next.join(",") };
    });
  };

  const toggleEffect = (label) => {
    setState((s) => {
      const has = s.effects.includes(label);
      return { ...s, effects: has ? s.effects.filter((e) => e !== label) : [...s.effects, label] };
    });
  };
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testSendResult, setTestSendResult] = useState(null);
  const [err, setErr] = useState("");

  const generate = async () => {
    if (!state.theme.trim()) return alert("Enter a theme first (e.g., 'Peptide of the week: BPC-157').");
    setGenerating(true);
    try {
      // 1) Kick off background LLM generation — returns immediately with a task_id
      //    so we NEVER hit Cloudflare's 100s edge timeout on slow model responses.
      const { data: kickoff } = await api.post("/admin/campaigns/weekly/generate-draft", {
        theme: state.theme,
        featured_product_slug: state.featured_product_slug || null,
        tone: state.tone,
        product_discount_percent: state.product_discount_percent ? Number(state.product_discount_percent) : null,
        product_discount_code: state.product_discount_code || null,
        description: state.description || null,
        effects: state.effects && state.effects.length ? state.effects : null,
      });
      const taskId = kickoff.task_id;
      // 2) Poll status every 2s until the task reports done/failed
      let elapsed = 0;
      // Safety cap so a stuck task doesn't spin forever — 3 min ceiling.
      const MAX_MS = 180_000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        elapsed += 2000;
        if (elapsed > MAX_MS) {
          alert("Generation is taking longer than 3 minutes — please try again. The theme may need to be simpler.");
          break;
        }
        try {
          const { data: st } = await api.get(`/admin/campaigns/weekly/generate-draft/status/${taskId}`);
          if (st.status === "done") {
            setState((s) => ({ ...s, ...(st.result || {}) }));
            break;
          }
          if (st.status === "failed") {
            alert(`Generation failed: ${st.error || "unknown error"}`);
            break;
          }
          // else still running — loop again
        } catch (e) {
          alert(`Polling failed: ${e?.response?.data?.detail || e.message}`);
          break;
        }
      }
    } catch (e) {
      alert(`Generation failed to start: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!state.subject || !state.body_html) return alert("Subject and body are required.");
    setSaving(true);
    try {
      const { data: saved } = await api.post("/admin/campaigns/weekly/save", {
        id: state.id, subject: state.subject, preview_text: state.preview_text,
        body_html: state.body_html,
        featured_product_slug: (state.featured_product_slug || "").trim() || null,
        theme: state.theme,
        product_discount_percent: state.product_discount_percent ? Number(state.product_discount_percent) : null,
        product_discount_code: (state.product_discount_code || "").trim().toUpperCase() || null,
      });
      // NEW · If admin ticked "Select date/time" AND filled the picker, also
      // schedule the campaign for that specific moment. If not, save behaves
      // exactly as before — draft is stored and the existing Schedule button
      // on the campaign card uses the default weekly cadence.
      if (state.use_custom_schedule && state.custom_scheduled_for) {
        try {
          const isoLocal = new Date(state.custom_scheduled_for).toISOString();
          await api.post(`/admin/campaigns/${saved.id || state.id}/schedule`, {
            custom_scheduled_for: isoLocal,
          });
        } catch (e) {
          alert(`Saved, but scheduling failed: ${apiErr(e)}`);
        }
      }
      onSaved();
    } catch (e) {
      alert(`Save failed: ${apiErr(e)}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur flex items-start justify-center p-6 overflow-y-auto" data-testid="weekly-draft-modal">
      <div className="bg-[#0C0C12] border border-white/10 max-w-3xl w-full my-8">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="font-heading text-lg">Weekly Campaign Draft</div>
          <button onClick={onClose} className="p-2 hover:bg-white/5"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* AI generation controls */}
          <div className="border border-purple-400/30 bg-purple-400/5 p-4 space-y-3">
            <div className="text-purple-300 font-mono text-[10px] uppercase tracking-wider">1. AI Draft</div>
            <input
              value={state.theme}
              onChange={(e) => setState((s) => ({ ...s, theme: e.target.value }))}
              placeholder="Weekly theme — e.g. 'Peptide of the week: BPC-157 mechanism'"
              className="w-full bg-[#05050A] border border-white/10 px-3 py-2 text-white text-sm font-mono outline-none focus:border-purple-400"
              data-testid="draft-theme-input"
            />
            <div className="flex gap-2">
              <select
                value={state.tone}
                onChange={(e) => setState((s) => ({ ...s, tone: e.target.value }))}
                className="bg-[#05050A] border border-white/10 px-3 py-2 text-white text-sm font-mono outline-none"
              >
                <option value="educational">Educational</option>
                <option value="conversational">Conversational</option>
                <option value="brief">Brief</option>
              </select>
              <button onClick={generate} disabled={generating || !state.theme.trim()} className="ml-auto px-4 py-2 border border-purple-400/50 bg-purple-400/10 text-purple-300 hover:bg-purple-400/20 font-mono text-[10px] uppercase tracking-wider disabled:opacity-40 flex items-center gap-2" data-testid="ai-generate-btn">
                {generating ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />}
                {generating ? "Generating…" : "Generate"}
              </button>
            </div>

            {/* Featured product picker — replaces the old free-text slug input.
                Multi-select (up to 3) so stack / bundle campaigns can show all
                component images. First selected = primary (CTA target). */}
            <div className="pt-2 border-t border-purple-400/20">
              <div className="flex items-center justify-between mb-2">
                <div className="text-purple-300 font-mono text-[9px] uppercase tracking-wider">
                  Featured products (up to 3) — first is CTA target
                </div>
                {selectedSlugs.length > 0 && (
                  <button
                    onClick={() => setState((s) => ({ ...s, featured_product_slug: "" }))}
                    className="text-[9px] font-mono text-[#5C657A] hover:text-red-400 uppercase tracking-wider"
                    data-testid="clear-featured-products-btn"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto border border-white/10 bg-[#05050A] p-2 flex flex-wrap gap-1.5">
                {productCatalog.length === 0 ? (
                  <div className="text-[10px] text-[#5C657A] font-mono">Loading products…</div>
                ) : (
                  productCatalog.map((p) => {
                    const isSelected = selectedSlugs.includes(p.slug);
                    const rank = selectedSlugs.indexOf(p.slug);
                    return (
                      <button
                        key={p.slug}
                        type="button"
                        onClick={() => toggleFeaturedProduct(p.slug)}
                        className={`px-2 py-1 text-[10px] font-mono border transition-colors ${
                          isSelected
                            ? "border-purple-400 bg-purple-400/20 text-purple-200"
                            : "border-white/10 bg-transparent text-[#B8C0CE] hover:border-purple-400/50 hover:text-white"
                        }`}
                        data-testid={`featured-product-toggle-${p.slug}`}
                      >
                        {isSelected && (
                          <span className="mr-1 text-purple-300">{rank === 0 ? "★" : `${rank + 1}·`}</span>
                        )}
                        {p.name}
                      </button>
                    );
                  })
                )}
              </div>
              {selectedSlugs.length > 0 && (
                <div className="mt-1.5 text-[10px] text-[#5C657A] font-mono">
                  Order: {selectedSlugs.join(" → ")} · CTA button links to <span className="text-purple-300">{selectedSlugs[0]}</span>
                </div>
              )}
            </div>

            {/* Product-specific weekly discount — optional, rendered as a callout inside the email body */}
            <div className="flex gap-2 items-center pt-2 border-t border-purple-400/20">
              <div className="text-purple-300 font-mono text-[9px] uppercase tracking-wider whitespace-nowrap">Optional discount →</div>
              <input
                type="number"
                min="1" max="90"
                value={state.product_discount_percent}
                onChange={(e) => setState((s) => ({ ...s, product_discount_percent: e.target.value }))}
                placeholder="%"
                className="w-20 bg-[#05050A] border border-white/10 px-3 py-2 text-white text-sm font-mono outline-none focus:border-purple-400"
                data-testid="draft-discount-percent"
              />
              <input
                value={state.product_discount_code}
                onChange={(e) => setState((s) => ({ ...s, product_discount_code: e.target.value.toUpperCase() }))}
                placeholder="Discount code (e.g. BPC15)"
                className="flex-1 bg-[#05050A] border border-white/10 px-3 py-2 text-white text-sm font-mono outline-none focus:border-purple-400 uppercase"
                data-testid="draft-discount-code"
              />
            </div>
            <div className="text-[10px] text-[#5C657A] font-mono leading-relaxed">
              Both fields required to include an offer. Ensure the code exists in Admin → Discounts before scheduling; the AI will render it as a prominent callout in the email body.
            </div>

            {/* Long-form product description — authoritative source of truth for the AI.
                Keeps the model grounded in the SPECIFIC benefits / positioning the admin
                wants to communicate, so the email talks about what actually matters. */}
            <div className="pt-2 border-t border-purple-400/20 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-purple-300 font-mono text-[9px] uppercase tracking-wider">Real-world effects (steers the voice)</div>
                <div className="text-[9px] text-[#5C657A] font-mono">{state.effects.length} selected</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {effectsCatalog.map((label) => {
                  const active = state.effects.includes(label);
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => toggleEffect(label)}
                      className={`px-2.5 py-1 text-[10px] font-mono border transition-colors ${
                        active
                          ? "bg-purple-400/20 border-purple-400 text-purple-200"
                          : "border-white/15 text-[#8A94A6] hover:border-purple-400/60 hover:text-purple-300"
                      }`}
                      data-testid={`effect-chip-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    >
                      {active ? "✓ " : "+ "}{label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-[#5C657A] font-mono leading-relaxed">
                Pick the real-world effects your audience cares about. The AI will write the email in plain, benefit-forward language around these — no clinical/receptor speak.
              </div>
            </div>

            <div className="pt-2 border-t border-purple-400/20 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-purple-300 font-mono text-[9px] uppercase tracking-wider">Optional description (grounds the AI)</div>
                <div className="text-[9px] text-[#5C657A] font-mono">{state.description ? `${state.description.length} chars` : ""}</div>
              </div>
              <textarea
                value={state.description}
                onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
                rows={6}
                maxLength={6000}
                placeholder={"Paste product benefits, mechanism, target audience, positioning — anything you want the AI to communicate. Example:\n\nKLOW is a physician-guided peptide stack combining GHK-Cu, KPV, BPC-157, and TB-500. Designed to accelerate tissue repair, reduce inflammation, promote cellular renewal, and support gut health & anti-aging. Users typically see results within 2-3 weeks…"}
                className="w-full bg-[#05050A] border border-white/10 px-3 py-2 text-white text-sm outline-none focus:border-purple-400 resize-y leading-relaxed"
                data-testid="draft-description-input"
              />
              <div className="text-[10px] text-[#5C657A] font-mono leading-relaxed">
                Optional — but strongly recommended. When present, the AI is instructed to treat this as the single source of truth and NOT invent claims outside of it. Compliance rules (no dosing, no route of administration) still apply and will be auto-enforced.
              </div>
            </div>
          </div>

          {/* Editable draft */}
          <div className="space-y-3">
            <div className="text-[#8A94A6] font-mono text-[10px] uppercase tracking-wider">2. Review & Edit</div>

            {state.compliance_warnings?.length > 0 && (
              <div className="border border-amber-400/40 bg-amber-400/5 p-3 text-xs text-amber-300 flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                Compliance warnings — please review body: {state.compliance_warnings.join(", ")}
              </div>
            )}

            <input
              value={state.subject}
              onChange={(e) => setState((s) => ({ ...s, subject: e.target.value }))}
              placeholder="Subject line"
              className="w-full bg-[#05050A] border border-white/10 px-3 py-2 text-white text-sm outline-none focus:border-[#00C3FF]"
              data-testid="draft-subject"
            />
            <input
              value={state.preview_text}
              onChange={(e) => setState((s) => ({ ...s, preview_text: e.target.value }))}
              placeholder="Preview text (inbox preheader)"
              className="w-full bg-[#05050A] border border-white/10 px-3 py-2 text-[#8A94A6] text-sm outline-none focus:border-[#00C3FF]"
            />
            <textarea
              value={state.body_html}
              onChange={(e) => setState((s) => ({ ...s, body_html: e.target.value }))}
              placeholder="Body HTML (paragraphs, headings, lists — will be wrapped in branded shell)"
              rows={14}
              className="w-full bg-[#05050A] border border-white/10 px-3 py-2 text-white text-xs font-mono outline-none focus:border-[#00C3FF]"
              data-testid="draft-body"
            />

            {/* NEW · Optional custom hero image upload. When a file is picked,
                it's uploaded to /api/admin/campaigns/upload-image and the
                returned URL is prepended to body_html as an <img> tag. If the
                admin never picks a file, campaign behavior is 100% unchanged. */}
            <div className="border border-white/10 bg-[#05050A] p-3 flex items-center gap-3">
              <label
                className="px-3 py-2 border border-[#00C3FF]/50 text-[#00C3FF] font-mono text-[10px] uppercase tracking-wider cursor-pointer hover:bg-[#00C3FF]/10 flex items-center gap-2"
                data-testid="campaign-image-upload-btn"
                title="Optional — attach a hero image (PNG/JPG/WEBP/GIF, max 8 MB) that will appear at the top of the email"
              >
                <Upload size={12} />
                {state.hero_image_url ? "Change Hero Image" : "Upload Hero Image"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const { data } = await api.post("/admin/campaigns/upload-image", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                      });
                      const absUrl = (window.location.origin || "") + data.url;
                      // Prepend the image at the top of the body_html; admin
                      // can move/edit/remove the tag directly in the textarea.
                      setState((s) => ({
                        ...s,
                        hero_image_url: absUrl,
                        body_html: `<div style="text-align:center;margin:0 0 20px;"><img src="${absUrl}" alt="ProSource Labs" style="width:100%;max-width:600px;border-radius:12px;" /></div>\n` + (s.body_html || ""),
                      }));
                    } catch (err) {
                      // eslint-disable-next-line no-alert
                      alert("Image upload failed: " + (err?.response?.data?.detail || err.message));
                    } finally {
                      e.target.value = "";
                    }
                  }}
                />
              </label>
              {state.hero_image_url && (
                <span className="text-[10px] font-mono text-emerald-400 truncate flex-1" title={state.hero_image_url}>
                  ✓ Image ready — embedded at top of body
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-white/10">
          {/* Left: test-send button. Fires the campaign ONLY to admin's inbox
              using the exact same code path as a real send. Requires the
              draft to be saved first (needs a real campaign id). */}
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                setSendingTest(true); setErr(""); setTestSendResult(null);
                try {
                  // ALWAYS auto-save before firing the test — even if the
                  // draft already has an id. Otherwise edits made since the
                  // last save (e.g. adding a featured product slug or fixing
                  // a discount code) never make it into the test email. This
                  // was the root cause of "the discount box has no image or
                  // product link" — user typed the slug, hit Send Test, but
                  // the send-test path re-read the stale DB record.
                  const { data: saved } = await api.post("/admin/campaigns/weekly/save", {
                    id: state.id, subject: state.subject, preview_text: state.preview_text,
                    body_html: state.body_html,
                    featured_product_slug: (state.featured_product_slug || "").trim() || null,
                    theme: state.theme,
                    product_discount_percent: state.product_discount_percent ? Number(state.product_discount_percent) : null,
                    product_discount_code: (state.product_discount_code || "").trim().toUpperCase() || null,
                  });
                  const campaignId = saved.id;
                  if (!state.id) setState((s) => ({ ...s, id: campaignId }));
                  const { data } = await api.post(`/admin/campaigns/${campaignId}/send-test`);
                  setTestSendResult(data);
                } catch (e) {
                  setErr(apiErr(e, "Test send failed."));
                } finally {
                  setSendingTest(false);
                }
              }}
              disabled={sendingTest || !state.subject || !state.body_html}
              className="px-4 py-2 border border-amber-400/60 text-amber-300 font-mono text-[10px] uppercase tracking-wider disabled:opacity-40 hover:bg-amber-400/10 flex items-center gap-2"
              data-testid="send-test-btn"
              title="Sends this exact email ONLY to your admin address. Nothing goes to real customers."
            >
              {sendingTest ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
              Send Test to Admin
            </button>
            {testSendResult && (
              <span className={`text-[10px] font-mono ${testSendResult.ok ? "text-emerald-400" : "text-red-400"}`}>
                {testSendResult.ok ? `✓ Sent to ${testSendResult.sent_to} — check inbox` : `✗ ${testSendResult.note || "Failed"}`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* NEW · Custom schedule checkbox. Unchecked = existing flow
                (save as draft, use default weekly cadence via card button).
                Checked = show datetime picker + schedule on save. */}
            <label className="flex items-center gap-2 text-[10px] font-mono text-[#8A94A6] uppercase tracking-wider cursor-pointer" data-testid="campaign-custom-schedule-checkbox">
              <input
                type="checkbox"
                checked={state.use_custom_schedule}
                onChange={(e) => setState((s) => ({ ...s, use_custom_schedule: e.target.checked }))}
                className="accent-[#00C3FF]"
              />
              <Clock size={12} className="text-[#00C3FF]" />
              Select date/time
            </label>
            {state.use_custom_schedule && (
              <input
                type="datetime-local"
                value={state.custom_scheduled_for}
                onChange={(e) => setState((s) => ({ ...s, custom_scheduled_for: e.target.value }))}
                className="bg-[#05050A] border border-[#00C3FF]/50 text-white text-[11px] font-mono px-2 py-1.5 outline-none focus:border-[#00C3FF]"
                data-testid="campaign-custom-schedule-input"
              />
            )}
            <button onClick={onClose} className="px-4 py-2 border border-white/20 text-white text-[10px] font-mono uppercase tracking-wider">Cancel</button>
            <button onClick={save} disabled={saving || !state.subject || !state.body_html} className="px-5 py-2 bg-[#00C3FF] text-black font-mono text-[10px] uppercase tracking-wider disabled:opacity-40 flex items-center gap-2" data-testid="save-draft-btn">
              {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
              {state.use_custom_schedule && state.custom_scheduled_for ? "Save & Schedule" : "Save Draft"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

